// Typed wrappers around the My.Tms endpoints. Types are hand-written here
// (vs. openapi-typescript on @my/directory-api) — when the Tms swagger is
// regularly published we'll switch to a generated schema for the same reasons
// it lives behind one in directory-api: keep DTOs in sync with the BE without
// re-typing every field. The runtime is the shared @my/platform/http
// transport so bearer token, X-App-Key, X-Idempotency-Key, and Result<T> error
// mapping all behave identically to every other call in the suite.
import { request } from '@my/platform/http';

// ────────────────────────────────────────────────────────────────────────────
// Enum value unions — the Tms BE uses JsonStringEnumConverter, so enums travel
// as their member names verbatim (NOT numeric values). Keep these unions in
// lock-step with the C# enums under My.Tms.Domain.Enums.
// ────────────────────────────────────────────────────────────────────────────

/** `BaseStatus` enum — applies to most catalog entities. */
export type BaseStatusValue = 'Active' | 'Inactive';

/**
 * `ServiceTagArea` — functional area of TMS a tag opts into. Stored on the
 * BE as a comma-joined list of these names; null/empty means "applies to
 * every area" (no `All` sentinel — the BE doesn't carry one).
 *
 * Order here mirrors the C# enum declaration order (Cargo=1 … Resources=10)
 * so the FE's canonical ordering matches what `ServiceTagAreas.Canonicalize`
 * emits when persisting. Don't reorder without a BE change.
 *
 * `ChargesAndCosts` is intentionally a single combined area on both sides —
 * operators tag charges and their matching costs together in nearly every
 * real workflow, so two separate areas was extra clicks for no signal.
 */
export type ServiceTagAreaValue =
  | 'Cargo'
  | 'ChargesAndCosts'
  | 'Customers'
  | 'Drivers'
  | 'Equipment'
  | 'Locations'
  | 'Orders'
  | 'Suppliers'
  | 'Tasks'
  | 'Resources';

/** Display order — matches the BE enum so canonical strings round-trip. */
export const SERVICE_TAG_AREAS: readonly ServiceTagAreaValue[] = [
  'Cargo',
  'ChargesAndCosts',
  'Customers',
  'Drivers',
  'Equipment',
  'Locations',
  'Orders',
  'Suppliers',
  'Tasks',
  'Resources',
] as const;

/**
 * Wire-name → human label map. Most areas display fine as-is, but
 * `ChargesAndCosts` reads better as `Charges & Costs`. Keep this map small
 * and lazy — only override the names that actually need it.
 */
const SERVICE_TAG_AREA_LABEL_OVERRIDES: Partial<Record<ServiceTagAreaValue, string>> = {
  ChargesAndCosts: 'Charges & Costs',
};

/**
 * Operator-facing label for a `ServiceTagAreaValue`. Falls back to the wire
 * name (`Cargo`, `Drivers`, …) so adding a new area on the BE doesn't
 * require an FE change before it renders sensibly.
 */
export function getServiceTagAreaLabel(area: ServiceTagAreaValue): string {
  return SERVICE_TAG_AREA_LABEL_OVERRIDES[area] ?? area;
}

// ────────────────────────────────────────────────────────────────────────────
// ServiceTag DTOs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Wire-format projection of `My.Tms.Domain.Entities.ServiceTag`.
 * `applyToServiceTypes` stays as the raw comma-joined string so the FE owns
 * parsing and rendering — keeps this DTO compatible with the storage shape.
 */
export interface ServiceTag {
  id: string;
  organisationId: string;
  name: string;
  description: string | null;
  /**
   * Comma-joined `ServiceTagArea` names (e.g. "Drivers,Equipment"), or
   * null/empty to mean "applies to every area". The BE canonicalises to
   * enum-declaration order on save, so callers can compare raw strings to
   * detect "actually changed".
   */
  applyToServiceTypes: string | null;
  /**
   * BE-seeded tags. The FE hides destructive controls when this is true —
   * the BE delete handler also returns Conflict if you try anyway, so this
   * is defence-in-depth UX rather than a security boundary.
   */
  isSystemTag: boolean;
  status: BaseStatusValue;
  createdUtc: string;
  updatedUtc: string | null;
}

/** POST `/tms/api/v1/serviceTags` body. */
export interface CreateServiceTagRequest {
  organisationId: string;
  name: string;
  description?: string | null;
  applyToServiceTypes?: string | null;
  /**
   * Always `false` from the FE — the field exists on the command so the
   * eventual seed-migration path can flip it, but operators don't get to
   * mark their own tags as system. Sent explicitly for clarity rather than
   * omitted (the BE defaults to false when missing).
   */
  isSystemTag?: boolean;
  status?: BaseStatusValue;
}

/**
 * PUT `/tms/api/v1/serviceTags` body. Note `isSystemTag` is intentionally
 * absent — the BE command rejects updates to that flag, and re-sending it
 * here would be misleading.
 */
export interface UpdateServiceTagRequest {
  serviceTagId: string;
  name: string;
  description?: string | null;
  applyToServiceTypes?: string | null;
  status?: BaseStatusValue;
}

// ────────────────────────────────────────────────────────────────────────────
// Trip Checkpoints
// ────────────────────────────────────────────────────────────────────────────
//
// A TEMPLATE switches on when an order matches its conditions (cargo type,
// cargo definition, service tag, cargo property — any one within a list, every
// list that is used) and carries SECTIONS: one for the order as a whole, and
// any number for kinds of stop (task types). Several templates can match one
// order; their checkpoints merge. The server assembles that (preview below), so
// the screen and the copy sent to a driver come from one implementation.
//
// Nothing blocks the planner: "Required" on a planner checkpoint is a warning
// while it isn't done; on the phone (driver / sub) it is a must-do. A failed
// check only ever warns.

export type TripCheckpointKindValue =
  | 'Confirm'
  | 'YesNo'
  | 'Photo'
  | 'Document'
  | 'Signature'
  | 'Quantity'
  | 'Identifier'
  | 'Temperature'
  | 'Inspection'
  | 'Reading'
  | 'Note';

/** Who signs a checkpoint off. */
export type TripCheckpointRoleValue = 'Planner' | 'Driver' | 'Sub';

export type TripCheckpointSectionScopeValue = 'Order' | 'Task';

/** Order sections: the order status it must be done by (a warning, never a block). */
export type TripCheckpointDueValue =
  | 'BeforeOpen'
  | 'BeforeInProgress'
  | 'BeforeCompleted'
  | 'BeforeInvoiceApproval';

/** Task sections: when, during the stop. */
export type TripCheckpointMomentValue = 'OnArrival' | 'During' | 'BeforeDeparture';

/** The order field a checkpoint updates with what was captured. */
export type TripCheckpointWriteBackValue =
  | 'None'
  | 'CargoIdentifier'
  | 'Seal'
  | 'ActualQuantity'
  | 'PodSignedBy'
  | 'PodDocument';

/** What a cargo definition carries — the "Cargo has" condition. */
export type CargoPropertyValue = 'Temperature' | 'Bic' | 'Compartments' | 'Products' | 'Compliance';

export interface TripCheckpoint {
  id: string;
  position: number;
  label: string;
  hint: string | null;
  kind: TripCheckpointKindValue;
  roles: TripCheckpointRoleValue[];
  required: boolean;
  /** Order sections only. */
  due: TripCheckpointDueValue | null;
  /** Task sections only. */
  moment: TripCheckpointMomentValue | null;
  writeBack: TripCheckpointWriteBackValue;
  waivable: boolean;
  /** Per-kind settings as a JSON object string, or null. */
  configJson: string | null;
}

export interface TripCheckpointSection {
  id: string;
  scope: TripCheckpointSectionScopeValue;
  /** Task sections: "At collection". Null on the order section. */
  name: string | null;
  position: number;
  /** Task sections: the task types it runs at. */
  taskTypes: TaskTypeValue[];
  checkpoints: TripCheckpoint[];
}

/** Wire-format projection of `My.Tms.Domain.Entities.TripCheckpointTemplate`. */
export interface TripCheckpointTemplate {
  id: string;
  organisationId: string;
  name: string;
  description: string | null;
  status: BaseStatusValue;
  /** Conditions — an empty list is no condition on that. */
  cargoTypes: CargoTypeValue[];
  cargoDefinitionIds: string[];
  serviceTagIds: string[];
  cargoProperties: CargoPropertyValue[];
  sections: TripCheckpointSection[];
  createdUtc: string;
  updatedUtc: string | null;
}

/** A checkpoint as sent on create / update. No `id` = new. */
export interface TripCheckpointInput {
  id: string | null;
  label: string;
  hint: string | null;
  kind: TripCheckpointKindValue;
  roles: TripCheckpointRoleValue[];
  required: boolean;
  due: TripCheckpointDueValue | null;
  moment: TripCheckpointMomentValue | null;
  writeBack: TripCheckpointWriteBackValue;
  waivable: boolean;
  configJson: string | null;
}

/** A section as sent on create / update. No `id` = new. Position = list order. */
export interface TripCheckpointSectionInput {
  id: string | null;
  scope: TripCheckpointSectionScopeValue;
  name: string | null;
  taskTypes: TaskTypeValue[];
  checkpoints: TripCheckpointInput[];
}

/** The template fields shared by create, update and a preview draft. */
export interface TripCheckpointTemplateFields {
  name: string;
  description: string | null;
  status: BaseStatusValue;
  cargoTypes: CargoTypeValue[];
  cargoDefinitionIds: string[];
  serviceTagIds: string[];
  cargoProperties: CargoPropertyValue[];
  sections: TripCheckpointSectionInput[];
}

/** POST `/tms/api/v1/tripCheckpointTemplates` body. */
export interface CreateTripCheckpointTemplateRequest extends TripCheckpointTemplateFields {
  organisationId: string;
}

/**
 * PUT `/tms/api/v1/tripCheckpointTemplates` body. Whole-object: every field is
 * required, and the sections and checkpoints replace the stored ones (matched
 * by `id`).
 */
export interface UpdateTripCheckpointTemplateRequest extends TripCheckpointTemplateFields {
  tripCheckpointTemplateId: string;
}

/**
 * POST `/tms/api/v1/tripCheckpointTemplates/preview` body: what an order would
 * get from every saved template — with `draft` standing in for the saved
 * template of the same id (or added, when it has none). Nothing is saved.
 */
export interface TripCheckpointPreviewRequest {
  organisationId: string;
  orderId: string;
  draft: (TripCheckpointTemplateFields & { id: string | null }) | null;
}

export interface TripCheckpointAssembledItem {
  key: string;
  checkpoint: TripCheckpoint;
  /** Templates it came from — more than one when two ask the same thing. */
  sources: string[];
  /** Who is actually asked on this order or stop. */
  askedOf: TripCheckpointRoleValue[];
  /** Set when nobody is left to ask, saying why. */
  skipped: string | null;
}

export interface TripCheckpointPreview {
  facts: {
    cargoType: CargoTypeValue | null;
    cargoDefinitionId: string;
    cargoDefinitionName: string;
    serviceTagIds: string[];
    serviceTagNames: string[];
    cargoProperties: CargoPropertyValue[];
    orderNumber: string;
    /** A Directory party — named on screen the way the order screens do. */
    customerPartyId: string;
  };
  matches: {
    templateId: string | null;
    templateName: string;
    matched: boolean;
    reason: string;
    /** Checkpoints it added before merging — 0 when it matched but has nothing for these stops. */
    contributes: number;
  }[];
  order: TripCheckpointAssembledItem[];
  tasks: {
    orderTaskId: string;
    sequence: number;
    taskType: TaskTypeValue;
    /** Null = not assigned yet, so either could be asked. */
    doneBy: 'Driver' | 'Sub' | null;
    who: string | null;
    /** Where the stop is — a Directory address id, named on screen. */
    addressId: string | null;
    items: TripCheckpointAssembledItem[];
  }[];
  asked: { planner: number; driver: number; sub: number };
}

// ────────────────────────────────────────────────────────────────────────────
// Order checkpoints — the assembled list on one order, and what people did
// ────────────────────────────────────────────────────────────────────────────
//
// Live: assembled from the templates on every read, like the preview. Only what
// people DO is stored — a planner's Done (with an answer or value), Not
// applicable / Waived with a reason, and one-offs added to this order. Nothing
// blocks the planner; "dueState" is a warning.

export type OrderCheckpointOutcomeValue = 'Done' | 'Waived' | 'NotApplicable';
/** `Skipped` = nobody is asked on this order or stop (the item's `skipped` says why). */
export type OrderCheckpointStateValue = 'Open' | 'Done' | 'Waived' | 'NotApplicable' | 'Skipped';
export type OrderCheckpointDueStateValue = 'Later' | 'DueNext' | 'Overdue';

export interface OrderCheckpointRecord {
  id: string;
  outcome: OrderCheckpointOutcomeValue;
  /** Who signed a Done; null for Waived / Not applicable, which cover the whole item. */
  role: TripCheckpointRoleValue | null;
  answer: boolean | null;
  valueText: string | null;
  /** A temperature, a count or a reading. */
  numberValue?: number | null;
  reason: string | null;
  recordedByName: string;
  recordedUtc: string;
  source: 'Office' | 'Phone';
  /** The job it was recorded on, from the phone. */
  dispatchId?: string | null;
  /** Photos and signatures taken with it. */
  attachments?: OrderCheckpointAttachment[];
}

/** A photo or signature the driver sent with a checkpoint answer. */
export interface OrderCheckpointAttachment {
  id: string;
  kind: 'Photo' | 'Signature';
  contentType: string;
}

export interface OrderCheckpointItem extends TripCheckpointAssembledItem {
  /** Set when it came from a one-off on this order (so it can be removed). */
  adhocId: string | null;
  state: OrderCheckpointStateValue;
  /** One per asked role. */
  signOffs: { role: TripCheckpointRoleValue; done: boolean; record: OrderCheckpointRecord | null }[];
  /** The Waived / Not applicable record. */
  skip: OrderCheckpointRecord | null;
  /** Order-level items only: against the order's next status. */
  dueState: OrderCheckpointDueStateValue | null;
}

export interface OrderCheckpoints {
  orderId: string;
  orderNumber: string;
  orderStatus: OrderStatusValue;
  invoicingStatus: string;
  customerPartyId: string;
  facts: TripCheckpointPreview['facts'];
  matches: TripCheckpointPreview['matches'];
  order: OrderCheckpointItem[];
  tasks: (Omit<TripCheckpointPreview['tasks'][number], 'items'> & { items: OrderCheckpointItem[] })[];
  summary: {
    open: number;
    openRequired: number;
    plannerOpenRequired: number;
    onSiteOpenRequired: number;
    dueNext: number;
    overdue: number;
  };
}

/** Where on the order an action applies: the order (no task) or one stop. */
interface OrderCheckpointTarget {
  organisationId: string;
  orderTaskId: string | null;
  key: string;
}

export interface MarkOrderCheckpointDoneRequest extends OrderCheckpointTarget {
  /** Yes / No and Pass / Fail kinds only. */
  answer: boolean | null;
  valueText: string | null;
}

export interface SkipOrderCheckpointRequest extends OrderCheckpointTarget {
  /** Not applicable: any planner. Waived: Manager and above. Both need a reason. */
  outcome: 'Waived' | 'NotApplicable';
  reason: string;
}

export interface AddOrderCheckpointRequest {
  organisationId: string;
  /** Null = an order-level one-off (needs `due`); a stop's id = at that stop (needs `moment`). */
  orderTaskId: string | null;
  label: string;
  hint: string | null;
  kind: TripCheckpointKindValue;
  roles: TripCheckpointRoleValue[];
  required: boolean;
  due: TripCheckpointDueValue | null;
  moment: TripCheckpointMomentValue | null;
  configJson: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Trip dispatch — sending stops to a driver's phone
// ────────────────────────────────────────────────────────────────────────────
//
// A dispatch is one send of a set of stops to one driver (their linked myDriver
// login), as a frozen copy. Sending again to the same driver is an update — a
// new revision they accept again. A stop is live with one driver at a time;
// moving it to another is a reassign. Query on the phone is a message thread.

export type TripDispatchStatusValue =
  | 'Sent'
  | 'Accepted'
  | 'Queried'
  | 'Completed'
  | 'Withdrawn'
  | 'Superseded';

export interface DispatchProposalStop {
  orderTaskId: string;
  sequence: number;
  taskType: TaskTypeValue;
  addressId: string | null;
  /**
   * The live job this stop is on — this driver's own one too. It is on ANOTHER
   * driver's job (sending it here is a reassign) only when it isn't
   * `liveDispatch.id` of the same proposal.
   */
  liveWith: { dispatchId: string; recipientName: string } | null;
}

export interface DispatchProposal {
  resourceId: string;
  recipientName: string;
  mobileUserId: string | null;
  canReceive: boolean;
  cannotReceiveReason: string | null;
  /** This driver's current job for this order — sending again is an update. */
  liveDispatch: { id: string; chainId: string; revision: number; status: TripDispatchStatusValue } | null;
  stops: DispatchProposalStop[];
}

/** POST `orders/{id}/dispatches/prepare` — who could get what, and what it looks like. */
export interface DispatchProposals {
  preview: TripCheckpointPreview;
  /** Null when Directory can't find the customer — the job still goes. */
  customerName: string | null;
  /** Address id → "Boston Scientific, Cork". */
  placeNames: Record<string, string>;
  mobileEnabled: boolean;
  proposals: DispatchProposal[];
  notSent: { orderTaskId: string; sequence: number; taskType: TaskTypeValue; addressId: string | null; reason: string }[];
}

export interface SendDispatchRequest {
  organisationId: string;
  resourceId: string;
  orderTaskIds: string[];
  message: string | null;
  /** Take stops that are live with another driver off their job. */
  reassign: boolean;
}

export interface OrderDispatch {
  id: string;
  chainId: string;
  revision: number;
  status: TripDispatchStatusValue;
  recipientName: string;
  resourceId: string;
  message: string | null;
  sentByName: string;
  sentUtc: string;
  acceptedUtc: string | null;
  closedUtc: string | null;
  stops: { orderTaskId: string; sequence: number; taskType: TaskTypeValue }[];
  /** The order has changed since this was sent — offer Send update. */
  changedSinceSent: boolean;
  unreadMessages: number;
  messageCount: number;
}

export interface OrderDispatches {
  dispatches: OrderDispatch[];
}

/** One conversation with a driver, across every order — the inbox row. */
export interface DriverInboxThread {
  chainId: string;
  /** The job's newest version. */
  dispatchId: string;
  orderId: string;
  /** Null when the job's frozen copy can't be read. */
  orderNumber: string | null;
  customerName: string | null;
  recipientName: string;
  status: TripDispatchStatusValue;
  sentByName: string;
  /** The caller sent (or updated) this job. */
  isMine: boolean;
  unread: number;
  messageCount: number;
  lastMessage: { fromRole: 'Planner' | 'Driver'; authorName: string; text: string; sentUtc: string } | null;
}

/** GET `dispatches/inbox` — counts over the whole organisation, threads by scope. */
export interface DriverInbox {
  mineUnread: number;
  allUnread: number;
  threads: DriverInboxThread[];
}

export interface DispatchMessage {
  id: string;
  fromRole: 'Planner' | 'Driver';
  authorName: string;
  text: string;
  sentUtc: string;
  /** Read by the other side. */
  readUtc: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Cargo Definition enums
// ────────────────────────────────────────────────────────────────────────────

/**
 * `CargoType` — top-level classification an operator picks when creating a
 * cargo definition. Values are the BE enum NAMES (BE uses
 * JsonStringEnumConverter); keep this union in lock-step with the C#
 * `My.Tms.Domain.Enums.CargoType` enum.
 *
 * <p>History: the variants + property-groups refactor (BE migration
 * 20260515150458) dropped <c>Fmcg</c> and <c>TemperatureControlled</c>
 * and added <c>Pharmaceutical</c>. Temperature is now a property group
 * applicable to any type (anything can be temperature-controlled).</p>
 */
export type CargoTypeValue =
  | 'FullLoad'
  | 'PartLoad'
  | 'IsoTank'
  | 'Container'
  | 'Pallet'
  | 'BulkMaterials'
  | 'BulkLiquids'
  | 'Livestock'
  | 'AgriGrains'
  | 'Construction'
  | 'Pharmaceutical';

/** Display order — matches the BE enum so canonical strings round-trip. */
export const CARGO_TYPES: readonly CargoTypeValue[] = [
  'FullLoad',
  'PartLoad',
  'IsoTank',
  'Container',
  'Pallet',
  'BulkMaterials',
  'BulkLiquids',
  'Livestock',
  'AgriGrains',
  'Construction',
  'Pharmaceutical',
] as const;

/**
 * Operator-facing labels. Wire-name → human label; falls back to the wire
 * name so adding a new cargo type on the BE renders sensibly until the FE
 * catches up.
 */
const CARGO_TYPE_LABELS: Record<CargoTypeValue, string> = {
  FullLoad: 'Full Load',
  PartLoad: 'Part Load',
  IsoTank: 'Iso Tank',
  Container: 'Container',
  Pallet: 'Pallet',
  BulkMaterials: 'Bulk Materials',
  BulkLiquids: 'Bulk Liquids',
  Livestock: 'Livestock',
  AgriGrains: 'Agri Grains',
  Construction: 'Construction',
  Pharmaceutical: 'Pharmaceutical',
};

/**
 * Aggregator cargo types — at order entry the operator adds items by
 * picking from other definitions, not from variants on the definition
 * itself. The BE validator enforces "no variants" for these; the FE
 * picker uses the same set to hide the Variants section.
 */
export const AGGREGATOR_CARGO_TYPES: readonly CargoTypeValue[] = ['FullLoad', 'PartLoad'] as const;

export function isAggregatorCargoType(t: CargoTypeValue): boolean {
  return AGGREGATOR_CARGO_TYPES.includes(t);
}

export function getCargoTypeLabel(t: CargoTypeValue): string {
  return CARGO_TYPE_LABELS[t] ?? t;
}

/**
 * `RatingUom` — unit of measure a cargo definition can be expressed in. A
 * definition carries one or more (e.g. PerPallet + PerLoadingMetre with a
 * conversion rule). Names mirror the C# `My.Tms.Domain.Enums.RatingUom`.
 */
export type RatingUomValue =
  | 'PerLoad'
  | 'PerContainer'
  | 'PerPallet'
  | 'PerTon'
  | 'PerCubicMetre'
  | 'PerLoadingMetre'
  | 'PerLitre'
  | 'PerKG'
  | 'PerHead';

export const RATING_UOMS: readonly RatingUomValue[] = [
  'PerLoad',
  'PerContainer',
  'PerPallet',
  'PerTon',
  'PerCubicMetre',
  'PerLoadingMetre',
  'PerLitre',
  'PerKG',
  'PerHead',
] as const;

const RATING_UOM_LABELS: Record<RatingUomValue, string> = {
  PerLoad: 'Per Load',
  PerContainer: 'Per Container',
  PerPallet: 'Per Pallet',
  PerTon: 'Per Ton',
  PerCubicMetre: 'Per Cubic Metre',
  PerLoadingMetre: 'Per Loading Metre',
  PerLitre: 'Per Litre',
  PerKG: 'Per KG',
  PerHead: 'Per Head',
};

export function getRatingUomLabel(u: RatingUomValue): string {
  return RATING_UOM_LABELS[u] ?? u;
}

// ────────────────────────────────────────────────────────────────────────────
// Cargo Definition DTOs (variants + property groups model)
// ────────────────────────────────────────────────────────────────────────────

/**
 * One variant under a `CargoDefinition` — what the operator picks at order
 * entry (e.g. "Euro Pallet", "Iso Tank 40ft", "Cattle", "GDP Refrigerated").
 *
 * <p>
 * Hybrid catalogue model: <c>presetKey</c> is set when the row came from
 * the global preset catalogue (see <c>cargoVariantPresets.ts</c>) — e.g.
 * <c>"euro"</c>, <c>"iso-40"</c>, <c>"gdp-refrig"</c>. <c>null</c> for
 * fully-custom variants the operator typed in. <c>name</c> is a snapshot
 * — copied from the preset at save time so a future catalogue rename
 * doesn't silently mutate existing rows.
 * </p>
 *
 * <p>
 * Variant-specific fields are per-type — only the subset relevant to
 * the parent's <c>cargoType</c> is populated:
 * Pallet → <c>loadingMetres</c>;
 * Iso Tank / Container → <c>perTrailer</c>;
 * Livestock → <c>maxTransitHours</c>;
 * Pharmaceutical → <c>minTempC</c>/<c>maxTempC</c>;
 * everything else with physical dimensions → L/W/H/Tare.
 * </p>
 */
export interface CargoDefinitionVariant {
  id: string;
  presetKey: string | null;
  name: string;
  sortOrder: number;

  // Pallet
  loadingMetres: number | null;

  // Iso Tank / Container
  perTrailer: number | null;

  // Livestock
  maxTransitHours: number | null;

  // Pharmaceutical
  minTempC: number | null;
  maxTempC: number | null;

  // Other / custom dimensional
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  tareWeightKg: number | null;
}

/**
 * Wire-format projection of `My.Tms.Domain.Entities.CargoDefinition`.
 *
 * <p>
 * <c>unitsOfMeasure</c> is the raw comma-joined string the BE persists
 * (e.g. <c>"PerPallet,PerLoadingMetre"</c>). The FE owns parsing — same
 * pattern as <c>ServiceTag.applyToServiceTypes</c>. The BE canonicalises
 * to enum-declaration order on save.
 * </p>
 *
 * <p>
 * <c>propertyGroupsJson</c> is the JSON document describing which
 * property groups (capacity, handling, temperature, stack, bic,
 * compartments, compliance) are ticked and what spec values they carry.
 * Returned raw so the FE parses into its own shape — see
 * <c>cargoPropertyGroups.ts</c> for the agreed schema. <c>null</c> when
 * no groups are ticked.
 * </p>
 */
export interface CargoDefinition {
  id: string;
  organisationId: string;
  cargoType: CargoTypeValue;
  displayName: string;
  /** Comma-joined `RatingUomValue` names — always at least one. */
  unitsOfMeasure: string;

  /** JSON document — see `cargoPropertyGroups.ts`. Null = no groups ticked. */
  propertyGroupsJson: string | null;

  /** Variants under this definition, ordered by `sortOrder`. Empty for aggregator types. */
  variants: CargoDefinitionVariant[];

  status: BaseStatusValue;

  /**
   * Catalogue keys for the org's `OrganisationEquipmentType` rows
   * (e.g. `trailer.standard_curtainsider`). The FE resolves to display
   * labels via the cached equipment catalogue from `@my/directory-api`.
   * Sorted by the BE for stable diffable payloads.
   */
  equipmentCatalogueKeys: string[];

  /** Ids of `ServiceTag` rows linked to this definition. Sorted by the BE. */
  serviceTagIds: string[];

  /**
   * The "Products" property — orders of this type carry a product. With
   * `products` linked, those are offered; with none linked, any of the
   * organisation's products.
   */
  hasProducts: boolean;

  /** Default accounting category CODE for lines of this cargo type; null = none. */
  accountingCategoryCode: string | null;

  /**
   * VAT rate (Directory tax schema id) for charges that follow the job, used
   * only when the VAT treatment lands on the standard line; null = no override.
   */
  taxRateId: string | null;

  /** Linked products in picker order; may be empty. */
  products: CargoDefinitionProduct[];

  createdUtc: string;
  updatedUtc: string | null;
}

/** A physical amount of product. Mass lands on a cargo row's weight, volume on its volume. */
export type ProductQuantityUnitValue = 'Kilograms' | 'Tonnes' | 'Litres' | 'CubicMetres';

export const PRODUCT_QUANTITY_UNITS: readonly ProductQuantityUnitValue[] = [
  'Kilograms',
  'Tonnes',
  'Litres',
  'CubicMetres',
];

/**
 * A Directory product linked to a cargo definition. `productName` is TMS's
 * copy, taken when it was linked; `defaultQuantity` + `quantityUnit` pre-fill
 * the cargo row at order entry.
 */
export interface CargoDefinitionProduct {
  productId: string;
  productName: string;
  defaultQuantity: number | null;
  quantityUnit: ProductQuantityUnitValue | null;
  /** This product's own category on this cargo type; null = inherits the definition's. */
  accountingCategoryCode: string | null;
  /** This product's VAT rate on this cargo type; null = inherits the definition's. */
  taxRateId: string | null;
  sortOrder: number;
}

/** One linked product on a create / update body. The list order is the picker order. */
export interface CargoDefinitionProductPayload {
  productId: string;
  productName: string;
  /** Needs `quantityUnit`. */
  defaultQuantity?: number | null;
  quantityUnit?: ProductQuantityUnitValue | null;
  /** Null / blank = inherit the definition's category. */
  accountingCategoryCode?: string | null;
  /** Null = inherit the definition's VAT rate. */
  taxRateId?: string | null;
}

/**
 * Strongly-typed C# `CargoDefinitionPropertyGroups` mirror. Persisted on
 * the BE as a JSON string (`propertyGroupsJson`); the helpers in
 * `cargoPropertyGroups.ts` parse + serialise this shape so the FE never
 * has to handraw the wire format.
 *
 * <p>
 * Each property is optional. Presence of a group key = "chip ticked";
 * values inside = spec applied to every order; missing values = prompt at
 * order entry. `bic` and `compartments` are pure markers — empty object
 * means "ticked".
 * </p>
 */
export interface CargoPropertyGroupsPayload {
  capacity?: {
    maxWeightKg?: number | null;
    maxVolumeM3?: number | null;
    maxPallets?: number | null;
  };
  handling?: {
    notes?: string | null;
  };
  temperature?: {
    minTempC?: number | null;
    maxTempC?: number | null;
    maxTransitDays?: number | null;
  };
  stack?: {
    maxStackHeightCm?: number | null;
    doubleStackPermitted?: boolean | null;
  };
  bic?: Record<string, never>;
  compartments?: Record<string, never>;
  compliance?: {
    standard?: string | null;
    certRef?: string | null;
    expiryDate?: string | null;
  };
}

/**
 * Per-variant payload on Create / Update commands. Mirrors the BE
 * `CargoDefinitionVariantInput` shape. The BE wholesale-replaces the
 * variant list on update, so Id stability across saves isn't a
 * requirement.
 */
export interface CargoDefinitionVariantPayload {
  presetKey?: string | null;
  name: string;
  sortOrder: number;

  loadingMetres?: number | null;
  perTrailer?: number | null;
  maxTransitHours?: number | null;
  minTempC?: number | null;
  maxTempC?: number | null;
  lengthCm?: number | null;
  widthCm?: number | null;
  heightCm?: number | null;
  tareWeightKg?: number | null;
}

/** POST `/tms/api/v1/cargoDefinitions` body. */
export interface CreateCargoDefinitionRequest {
  organisationId: string;
  cargoType: CargoTypeValue;
  displayName: string;
  unitsOfMeasure: string;

  /** Strong shape — BE deserialises and canonicalises before persisting. */
  propertyGroups?: CargoPropertyGroupsPayload | null;

  /**
   * Variants — required for non-aggregator types, must be empty for
   * aggregator types (FullLoad / PartLoad). The BE validator enforces.
   */
  variants?: CargoDefinitionVariantPayload[];

  status?: BaseStatusValue;

  equipmentCatalogueKeys?: string[];
  serviceTagIds?: string[];

  /** Defaults to false. `products` (none, one or more) is only accepted with it on. */
  hasProducts?: boolean;
  accountingCategoryCode?: string | null;
  taxRateId?: string | null;
  products?: CargoDefinitionProductPayload[];
}

/**
 * PUT `/tms/api/v1/cargoDefinitions` body. `cargoType` is required for
 * payload symmetry with the BE command, but the BE rejects any value that
 * disagrees with the row's stored type — the FE locks the type select on
 * edit so this is enforcement at both ends.
 */
export interface UpdateCargoDefinitionRequest {
  cargoDefinitionId: string;
  cargoType: CargoTypeValue;
  displayName: string;
  unitsOfMeasure: string;

  propertyGroups?: CargoPropertyGroupsPayload | null;

  variants?: CargoDefinitionVariantPayload[];

  /**
   * REQUIRED, unlike on create: the handler assigns it unconditionally and the
   * command defaults to Active, so leaving it out of an edit silently
   * re-activates a definition somebody retired. An editor that does not offer
   * the control echoes the row's own status back.
   */
  status: BaseStatusValue;

  equipmentCatalogueKeys?: string[];
  serviceTagIds?: string[];

  /**
   * Left out → the stored flag and list are kept (the BE applies only what was
   * sent). `hasProducts: false` clears the list.
   */
  hasProducts?: boolean;
  /** Left out → kept; explicit null / blank → cleared. */
  accountingCategoryCode?: string | null;
  /** Left out → kept; explicit null → cleared. */
  taxRateId?: string | null;
  products?: CargoDefinitionProductPayload[];
}

// ────────────────────────────────────────────────────────────────────────────
// Endpoint map
// ────────────────────────────────────────────────────────────────────────────
// Audit trail
// ────────────────────────────────────────────────────────────────────────────

/** What happened to a record. */
/** `Overridden` = a value the system proposed was changed by hand (an invoice line's VAT, nominal or category). */
export type AuditActionValue = 'Created' | 'Updated' | 'Deleted' | 'Restored' | 'Overridden';

/**
 * One entry in a record's history.
 *
 * Entries produced by the same save share a `changeSetId` — one click on Save is
 * one thing that happened, even when it touched five tables, and the timeline
 * groups on it.
 */
export interface AuditLogEntry {
  id: string;
  changeSetId: string;
  occurredUtc: string;
  /** Email, `svc:{client}` for an integration, or "system". */
  actorKey: string;
  /** The identity provider's subject; null for machine tokens. */
  actorId: string | null;
  /** Which record it belongs to — the Audit Log screen shows every kind at once. */
  aggregateType: string;
  aggregateId: string;
  entityType: string;
  entityId: string;
  action: AuditActionValue;
  /**
   * The fields that moved, as `{"Status":{"from":"Draft","to":"Open"}}`, or the
   * whole row under `to` / `from` for a create or a delete. Raw on purpose — the
   * client renders it, and a server-side shape would have to guess how every
   * field should read.
   */
  changesJson: string | null;
}

// ────────────────────────────────────────────────────────────────────────────

/** Endpoints scoped to the Tms API (`/tms/api/v1/*`). */
export const tmsApi = {
  /**
   * A record's history, newest first — its own changes and its children's.
   * Aggregate types: Order, Invoice, Load, CargoDefinition.
   *
   * Permission follows the record: whoever may read it may read what happened
   * to it, so no separate audit right has to be granted to the people whose job
   * is explaining the record.
   */
  getAuditTrail: (args: {
    organisationId: string;
    /** One type, several comma-separated, or omitted for everything readable. */
    aggregateType?: string;
    /**
     * One record, or omitted for every record of that type in the organisation —
     * how a settings screen asks "what changed in here lately", where there is
     * no single row to point at.
     */
    aggregateId?: string;
    take?: number;
    /**
     * Where the previous page stopped — the last entry's timestamp AND id.
     * A keyset rather than an offset: the table grows at the top, so an offset
     * would shift under the reader and repeat or skip a page.
     */
    beforeUtc?: string;
    beforeId?: string;
    /** All optional, all ANDed. Empty means no opinion, never "match nothing". */
    fromUtc?: string;
    toUtc?: string;
    actor?: string;
    action?: AuditActionValue;
    entityType?: string;
    search?: string;
  }) =>
    request.get<AuditLogEntry[]>(
      `/tms/api/v1/audit?organisationId=${encodeURIComponent(args.organisationId)}` +
        (args.aggregateType ? `&aggregateType=${encodeURIComponent(args.aggregateType)}` : '') +
        (args.aggregateId ? `&aggregateId=${encodeURIComponent(args.aggregateId)}` : '') +
        `&take=${args.take ?? 100}` +
        (args.beforeUtc && args.beforeId
          ? `&beforeUtc=${encodeURIComponent(args.beforeUtc)}&beforeId=${args.beforeId}`
          : '') +
        (args.fromUtc ? `&fromUtc=${encodeURIComponent(args.fromUtc)}` : '') +
        (args.toUtc ? `&toUtc=${encodeURIComponent(args.toUtc)}` : '') +
        (args.actor ? `&actor=${encodeURIComponent(args.actor)}` : '') +
        (args.action ? `&action=${args.action}` : '') +
        (args.entityType ? `&entityType=${encodeURIComponent(args.entityType)}` : '') +
        (args.search ? `&search=${encodeURIComponent(args.search)}` : ''),
    ),

  /** GET every service tag for an organisation. */
  getServiceTags: (organisationId: string) =>
    request.get<ServiceTag[]>(
      `/tms/api/v1/serviceTags?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** GET a single service tag by id. */
  getServiceTag: (id: string) => request.get<ServiceTag>(`/tms/api/v1/serviceTags/${id}`),

  /**
   * POST a new service tag. Returns the new id (BE wraps in Result<Guid>;
   * the BaseController unwraps to the bare value).
   */
  createServiceTag: (body: CreateServiceTagRequest) =>
    request.post<string>('/tms/api/v1/serviceTags', body),

  /** PUT an existing service tag. Returns the same id on success. */
  updateServiceTag: (body: UpdateServiceTagRequest) =>
    request.put<string>('/tms/api/v1/serviceTags', body),

  /**
   * DELETE a service tag by id. The BE returns 409 Conflict if the tag is
   * `isSystemTag` — the FE should hide the trash button for those rows so
   * the operator never sees a confusing toast.
   */
  deleteServiceTag: (id: string) => request.del<unknown>(`/tms/api/v1/serviceTags/${id}`),

  // ── Trip Checkpoints ─────────────────────────────────────────────────────

  /** GET every trip checkpoint template for an organisation, sections and checkpoints in order. */
  getTripCheckpointTemplates: (organisationId: string) =>
    request.get<TripCheckpointTemplate[]>(
      `/tms/api/v1/tripCheckpointTemplates?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** POST a new template. Returns the new id. */
  createTripCheckpointTemplate: (body: CreateTripCheckpointTemplateRequest) =>
    request.post<string>('/tms/api/v1/tripCheckpointTemplates', body),

  /** PUT a template, sections and checkpoints included. Returns the same id. */
  updateTripCheckpointTemplate: (body: UpdateTripCheckpointTemplateRequest) =>
    request.put<string>('/tms/api/v1/tripCheckpointTemplates', body),

  /** DELETE (soft) a template. Checkpoints already sent are copies and unaffected. */
  deleteTripCheckpointTemplate: (id: string) =>
    request.del<unknown>(`/tms/api/v1/tripCheckpointTemplates/${id}`),

  /** POST — the assembled checkpoints one order would get. Reads only. */
  previewTripCheckpoints: (body: TripCheckpointPreviewRequest) =>
    request.post<TripCheckpointPreview>('/tms/api/v1/tripCheckpointTemplates/preview', body),

  // ── Order checkpoints ────────────────────────────────────────────────────
  // Every action answers with the refreshed list, so the panel re-renders from
  // one response.

  getOrderCheckpoints: (organisationId: string, orderId: string) =>
    request.get<OrderCheckpoints>(
      `/tms/api/v1/orders/${orderId}/checkpoints?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** A photo or signature recorded on the phone, as bytes. */
  getOrderCheckpointAttachment: (organisationId: string, orderId: string, attachmentId: string) =>
    request.get<Blob>(
      `/tms/api/v1/orders/${orderId}/checkpoints/attachments/${attachmentId}?organisationId=${encodeURIComponent(organisationId)}`,
      { responseType: 'blob' },
    ),

  /**
   * Put a driver's photo or signature on the order's Documents as `type` —
   * linked to the same file. Again on the same file changes its type. Answers
   * with the document id.
   */
  attachOrderCheckpointAttachment: (
    orderId: string,
    attachmentId: string,
    body: { organisationId: string; type: OrderDocumentTypeValue },
  ) =>
    request.post<string>(`/tms/api/v1/orders/${orderId}/checkpoints/attachments/${attachmentId}/document`, body),

  markOrderCheckpointDone: (orderId: string, body: MarkOrderCheckpointDoneRequest) =>
    request.post<OrderCheckpoints>(`/tms/api/v1/orders/${orderId}/checkpoints/done`, body),

  skipOrderCheckpoint: (orderId: string, body: SkipOrderCheckpointRequest) =>
    request.post<OrderCheckpoints>(`/tms/api/v1/orders/${orderId}/checkpoints/skip`, body),

  undoOrderCheckpointRecord: (organisationId: string, orderId: string, recordId: string) =>
    request.del<OrderCheckpoints>(
      `/tms/api/v1/orders/${orderId}/checkpoints/records/${recordId}?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  addOrderCheckpoint: (orderId: string, body: AddOrderCheckpointRequest) =>
    request.post<OrderCheckpoints>(`/tms/api/v1/orders/${orderId}/checkpoints/adhoc`, body),

  // ── Trip dispatch ────────────────────────────────────────────────────────

  prepareDispatch: (organisationId: string, orderId: string) =>
    request.post<DispatchProposals>(`/tms/api/v1/orders/${orderId}/dispatches/prepare`, { organisationId }),

  getOrderDispatches: (organisationId: string, orderId: string) =>
    request.get<OrderDispatches>(
      `/tms/api/v1/orders/${orderId}/dispatches?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  sendDispatch: (orderId: string, body: SendDispatchRequest) =>
    request.post<OrderDispatches>(`/tms/api/v1/orders/${orderId}/dispatches`, body),

  withdrawDispatch: (organisationId: string, dispatchId: string) =>
    request.post<OrderDispatches>(`/tms/api/v1/dispatches/${dispatchId}/withdraw`, { organisationId }),

  getDriverInbox: (organisationId: string, scope: 'mine' | 'all') =>
    request.get<DriverInbox>(
      `/tms/api/v1/dispatches/inbox?organisationId=${encodeURIComponent(organisationId)}&scope=${scope}`,
    ),

  getDispatchMessages: (organisationId: string, chainId: string) =>
    request.get<DispatchMessage[]>(
      `/tms/api/v1/dispatches/chains/${chainId}/messages?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  postDispatchMessage: (organisationId: string, chainId: string, text: string) =>
    request.post<DispatchMessage[]>(`/tms/api/v1/dispatches/chains/${chainId}/messages`, { organisationId, text }),

  removeOrderCheckpoint: (organisationId: string, orderId: string, adhocId: string) =>
    request.del<OrderCheckpoints>(
      `/tms/api/v1/orders/${orderId}/checkpoints/adhoc/${adhocId}?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  // ── Cargo Definitions ────────────────────────────────────────────────────

  /** GET every cargo definition for an organisation. */
  getCargoDefinitions: (organisationId: string) =>
    request.get<CargoDefinition[]>(
      `/tms/api/v1/cargoDefinitions?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** POST a new cargo definition. Returns the new id. */
  createCargoDefinition: (body: CreateCargoDefinitionRequest) =>
    request.post<string>('/tms/api/v1/cargoDefinitions', body),

  /** PUT an existing cargo definition. Returns the same id on success. */
  updateCargoDefinition: (body: UpdateCargoDefinitionRequest) =>
    request.put<string>('/tms/api/v1/cargoDefinitions', body),

  /** DELETE (soft) a cargo definition by id. */
  deleteCargoDefinition: (id: string) => request.del<unknown>(`/tms/api/v1/cargoDefinitions/${id}`),

  // ── Charge / Cost Definitions (operator-facing label: "Charges & Costs") ─

  /** GET every charge / cost definition for an organisation. */
  getChargeCostDefinitions: (organisationId: string) =>
    request.get<ChargeCostDefinition[]>(
      `/tms/api/v1/chargeCostDefinitions?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** POST a new charge / cost definition. Returns the new id. */
  createChargeCostDefinition: (body: CreateChargeCostDefinitionRequest) =>
    request.post<string>('/tms/api/v1/chargeCostDefinitions', body),

  /** PUT an existing charge / cost definition. Returns the same id on success. */
  updateChargeCostDefinition: (body: UpdateChargeCostDefinitionRequest) =>
    request.put<string>('/tms/api/v1/chargeCostDefinitions', body),

  /**
   * DELETE (soft) a charge / cost definition by id. The BE returns 409
   * Conflict if the row is `isProtected` — the FE should hide the trash
   * button for those rows so the operator never sees a confusing toast.
   */
  deleteChargeCostDefinition: (id: string) =>
    request.del<unknown>(`/tms/api/v1/chargeCostDefinitions/${id}`),

  // ── Orders ──────────────────────────────────────────────────────────────

  /** GET every order for an organisation (newest first). */
  getOrders: (organisationId: string) =>
    request.get<Order[]>(`/tms/api/v1/orders?organisationId=${encodeURIComponent(organisationId)}`),

  /**
   * GET one page of the Orders screen — searched, narrowed to a status and
   * sorted on the server, so the order holds across pages. `page` is 1-based.
   */
  getOrdersPage: (params: OrdersPageParams & { page: number; pageSize: number }) => {
    const qs = new URLSearchParams({
      organisationId: params.organisationId,
      sort: params.sort ?? 'newest',
      descending: String(params.descending ?? false),
      page: String(params.page),
      pageSize: String(params.pageSize),
    });
    if (params.search?.trim()) qs.set('search', params.search.trim());
    if (params.status) qs.set('status', params.status);
    else if (params.activeOnly) qs.set('activeOnly', 'true');
    if (params.from) qs.set('from', params.from);
    if (params.to) qs.set('to', params.to);
    return request.get<OrdersPage>(`/tms/api/v1/orders/paged?${qs.toString()}`);
  },

  /**
   * GET one page of a customer's earlier orders for New order's "Start from a
   * previous order" list — latest run first (first task's planned date and
   * time), never Draft or Cancelled, narrowed to the picked cargo and variant
   * when given. `page` is 1-based.
   */
  getPreviousOrders: (params: PreviousOrdersParams & { page: number; pageSize: number }) => {
    const qs = new URLSearchParams({
      organisationId: params.organisationId,
      customerPartyId: params.customerPartyId,
      page: String(params.page),
      pageSize: String(params.pageSize),
    });
    if (params.cargoDefinitionId) qs.set('cargoDefinitionId', params.cargoDefinitionId);
    if (params.cargoDefinitionVariantId)
      qs.set('cargoDefinitionVariantId', params.cargoDefinitionVariantId);
    return request.get<PreviousOrdersPage>(`/tms/api/v1/orders/previous?${qs.toString()}`);
  },

  /** GET a single order by id, with the service-tag join hydrated. */
  getOrder: (id: string) => request.get<Order>(`/tms/api/v1/orders/${id}`),

  /**
   * POST a new order. Caller is expected to have already minted an order
   * number via `POST /directory/api/v1/organisationSequences/consume`
   * (see `useConsumeOrganisationSequence`). Returns the full created
   * `Order` so the FE can hydrate its detail view without an extra GET.
   */
  createOrder: (body: CreateOrderRequest) => request.post<Order>('/tms/api/v1/orders', body),

  /**
   * POST a new order built from a previous one — New order's "Use this
   * order". One save server-side: customer, cargo, service tags and the chosen
   * tasks always; the rest per the options. Returns the new `Order`.
   */
  copyOrder: (body: CopyOrderRequest) => request.post<Order>('/tms/api/v1/orders/copy', body),

  // ── Order templates ─────────────────────────────────────────────────────

  /** GET a customer's order templates, most used first. */
  getOrderTemplates: (organisationId: string, customerPartyId: string, includeArchived = false) =>
    request.get<OrderTemplate[]>(
      `/tms/api/v1/orderTemplates?organisationId=${encodeURIComponent(organisationId)}` +
        `&customerPartyId=${encodeURIComponent(customerPartyId)}&includeArchived=${includeArchived}`,
    ),

  /** GET a template as an order + tasks, for the New order panel. Dates sit on a fixed day 0. */
  getOrderTemplatePreview: (id: string) =>
    request.get<OrderTemplatePreview>(`/tms/api/v1/orderTemplates/${id}/preview`),

  /** POST — save an order (as last saved) as one of its customer's templates. */
  saveOrderTemplate: (body: { sourceOrderId: string; name: string; description: string | null }) =>
    request.post<OrderTemplate>('/tms/api/v1/orderTemplates', body),

  /** PUT — rename / describe. */
  updateOrderTemplate: (id: string, body: { name: string; description: string | null }) =>
    request.put<OrderTemplate>(`/tms/api/v1/orderTemplates/${id}`, body),

  archiveOrderTemplate: (id: string) =>
    request.post<OrderTemplate>(`/tms/api/v1/orderTemplates/${id}/archive`),

  restoreOrderTemplate: (id: string) =>
    request.post<OrderTemplate>(`/tms/api/v1/orderTemplates/${id}/restore`),

  deleteOrderTemplate: (id: string) => request.del<unknown>(`/tms/api/v1/orderTemplates/${id}`),

  /**
   * PUT an existing order. `orderNumber` is immutable post-create;
   * everything else (customer, status, cargo, BIC, temperature,
   * service tags) round-trips. Returns the updated `Order`.
   */
  updateOrder: (body: UpdateOrderRequest) => request.put<Order>('/tms/api/v1/orders', body),

  /** DELETE (soft) an order by id. */
  deleteOrder: (id: string) => request.del<unknown>(`/tms/api/v1/orders/${id}`),

  /**
   * DELETE a customer / supplier through TMS. 409 (naming the orders and
   * invoices) while the party is on any order but a cancelled one; otherwise
   * TMS asks Directory to delete it. Directory refuses deletes that don't come
   * this way, except from Stratum support.
   */
  deleteParty: (id: string) => request.del<unknown>(`/tms/api/v1/parties/${id}`),

  /**
   * GET the order's estimated laden CO2. Worked out by Directory and stored
   * against the order; the server replans only when the order's stops,
   * bookings, cargo or hazard flag (or the org's CO2 factors) changed. `null`
   * (204) when the order has fewer than two stops.
   */
  getOrderEmissions: (id: string) =>
    request
      .get<OrderEmissions | ''>(`/tms/api/v1/orders/${id}/emissions`)
      .then((r) => (r === '' ? null : r)),

  /**
   * POST up to 100 order ids. Orders with no journey (or no estimate yet) are
   * left out of `estimates`; `pendingOrderIds` still need working out — the
   * server does a few per call, so ask again.
   */
  getOrdersEmissions: (organisationId: string, orderIds: string[]) =>
    request.post<OrdersEmissions>('/tms/api/v1/orders/emissions', { organisationId, orderIds }),

  /**
   * POST a batch of fully-resolved order trees (header + tasks + cargo). Each
   * order commits independently; the response carries per-order outcomes so the
   * caller can retry only the failures. Order numbers must be pre-allocated
   * (see `useConsumeOrganisationSequence`).
   */
  importOrders: (body: ImportOrdersRequest) =>
    request.post<ImportOrdersResult>('/tms/api/v1/orders/import', body),

  /**
   * GET orders awaiting approval-for-invoicing (Completed + Pending), each with
   * charge totals and the live approval exceptions (ignore/warn/stop). The FE
   * resolves `customerPartyId` + the address ids to display text via Directory.
   */
  getOrdersForApproval: (organisationId: string) =>
    request.get<OrderForApproval[]>(
      `/tms/api/v1/orders/for-approval?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /**
   * POST: sign orders off for invoicing. Stop-level issues are rejected
   * server-side (warnings pass — the override); per-order outcome in the body.
   */
  approveOrdersForInvoicing: (body: ApproveOrdersForInvoicingRequest) =>
    request.post<ApproveOrdersForInvoicingResult>('/tms/api/v1/orders/approve-for-invoicing', body),

  /** GET approved orders with un-invoiced charges — the Create-invoices queue. */
  getOrdersForInvoicing: (organisationId: string) =>
    request.get<OrderForInvoicing[]>(
      `/tms/api/v1/orders/for-invoicing?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** POST: send an approved order back to For-approval (Approved → Pending). */
  unapproveOrderForInvoicing: (body: { organisationId: string; orderId: string }) =>
    request.post<unknown>('/tms/api/v1/orders/unapprove-for-invoicing', body),

  // ── Invoices ──────────────────────────────────────────────────────────────

  /** GET invoices for an org (summary rows; Overdue derived server-side). */
  getInvoices: (organisationId: string) =>
    request.get<InvoiceSummary[]>(
      `/tms/api/v1/invoices?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /** GET a single invoice with its lines. */
  getInvoice: (id: string) => request.get<Invoice>(`/tms/api/v1/invoices/${id}`),

  /** POST: generate an invoice from selected charge lines (+ ad-hoc). */
  generateInvoice: (body: GenerateInvoiceRequest) =>
    request.post<Invoice>('/tms/api/v1/invoices', body),

  /** POST: advance status — Draft→Issued, Issued→Sent, (Issued|Sent)→Paid. */
  /**
   * Void an invoice: releases the charges it consumed and drops the orders it
   * billed back to Approved. The server refuses a PAID invoice (credit it
   * instead), and an EXPORTED one until `accountingCancellationAcknowledged`
   * says it has been cancelled in the accounting system too.
   */
  cancelInvoice: (id: string, body: CancelInvoiceRequest) =>
    request.post<Invoice>(`/tms/api/v1/invoices/${id}/cancel`, body),

  /** Bill one charge to someone else (null = the order's payer). Locked once invoiced. */
  setChargeBillTo: (chargeCostId: string, partyId: string | null) =>
    request.put<OrderChargeCost>(`/tms/api/v1/orderChargeCosts/${chargeCostId}/bill-to`, { partyId }),

  /** Invoice the whole order to someone other than its customer (null = the customer). */
  setOrderInvoiceTo: (orderId: string, partyId: string | null) =>
    request.put<Order>(`/tms/api/v1/orders/${orderId}/invoice-to`, { partyId }),

  /** Mark an invoice exported to the accounting system, or take the mark back. */
  setInvoiceExport: (id: string, body: { exported: boolean; reference?: string | null }) =>
    request.post<Invoice>(`/tms/api/v1/invoices/${id}/export`, body),

  /** Edit a draft invoice or proforma. Refused once issued. */
  updateDraftInvoice: (id: string, body: UpdateDraftInvoiceRequest) =>
    request.put<Invoice>(`/tms/api/v1/invoices/${id}`, body),

  /** Credit all or part of an invoice (paid, or unpaid and exported). Returns the credit note. */
  raiseCreditNote: (id: string, body: RaiseCreditNoteRequest) =>
    request.post<Invoice>(`/tms/api/v1/invoices/${id}/credit-note`, body),

  setInvoiceStatus: (id: string, status: InvoiceStatusValue) =>
    request.post<Invoice>(`/tms/api/v1/invoices/${id}/status`, { status }),

  /**
   * POST: email the stored PDF to the customer's billing contacts and mark the
   * invoice sent (Issued → Sent; Sent / Paid stay put and just restamp). Serves
   * Send and Resend alike.
   *
   * No body. The server reads the billing contacts from Directory itself, as
   * the signed-in user, and uses the invoice's own `emailMessage` — so neither
   * the recipients nor the text can be chosen by the caller. Upload a fresh PDF
   * first: the server attaches whatever is stored.
   */
  sendInvoiceEmail: (id: string) =>
    request.post<Invoice>(`/tms/api/v1/invoices/${id}/email`),

  /** POST (multipart): store the rendered invoice PDF in blob. */
  uploadInvoicePdf: (id: string, file: Blob) => {
    const fd = new FormData();
    fd.append('File', file, `${id}.pdf`);
    return request.post<unknown>(`/tms/api/v1/invoices/${id}/pdf`, fd);
  },

  /**
   * GET the PDF stored when the invoice was generated — the issued document
   * itself, byte for byte.
   *
   * Returns `null` when the server has nothing stored (404), which is a real
   * state rather than a failure: invoices raised before the upload existed
   * have no blob, and the caller's answer is to render the document instead.
   * Anything else throws, so a permission or network problem is not silently
   * downgraded into a re-render that looks like it worked.
   */
  getInvoicePdfContent: async (id: string): Promise<Blob | null> => {
    try {
      return await request.get<Blob>(`/tms/api/v1/invoices/${id}/pdf/content`, {
        responseType: 'blob',
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  // ── Order Tasks ─────────────────────────────────────────────────────────

  /** GET every task for an order, with cargo items embedded. Ordered by `sequence` ascending. */
  getOrderTasks: (orderId: string) =>
    request.get<OrderTask[]>(`/tms/api/v1/orderTasks?orderId=${encodeURIComponent(orderId)}`),

  /** GET a single task by id, with cargo items embedded. */
  getOrderTask: (id: string) => request.get<OrderTask>(`/tms/api/v1/orderTasks/${id}`),

  /**
   * POST a new non-booking task. Booking halves (`BookingDeparture` /
   * `BookingArrival`) are rejected — create those via `createOrderBooking`.
   */
  createOrderTask: (body: CreateOrderTaskRequest) =>
    request.post<OrderTask>('/tms/api/v1/orderTasks', body),

  /** PUT an existing task. `type` and `bookingId` are immutable. */
  updateOrderTask: (body: UpdateOrderTaskRequest) =>
    request.put<OrderTask>('/tms/api/v1/orderTasks', body),

  /**
   * Move one leg along — started, done, or abandoned.
   *
   * Narrow on purpose: `updateOrderTask` replaces nineteen columns, so flipping
   * one enum through it would mean echoing every other value back and risking a
   * stale one. The server also owns what this means — completing a leg with no
   * recorded time stamps one, and it refuses a move backwards from a state the
   * actuals and the POD were recorded against.
   */
  setOrderTaskState: (taskId: string, state: TaskStateValue) =>
    request.put<OrderTask>(`/tms/api/v1/orderTasks/${taskId}/state`, { state }),

  // ── Order task resources (assignment) ───────────────────────────────────

  /** GET every assignment across an order's tasks, in task order. */
  getOrderResources: (orderId: string) =>
    request.get<OrderTaskResource[]>(
      `/tms/api/v1/orderTaskResources?orderId=${encodeURIComponent(orderId)}`,
    ),

  /**
   * GET every assignment in the organisation — the planning queue needs the crew
   * on every row at once. Unscoped by date, matching how `getOrders` works.
   */
  getOrganisationResources: (organisationId: string) =>
    request.get<OrderTaskResource[]>(
      `/tms/api/v1/orderTaskResources/organisation?organisationId=${encodeURIComponent(organisationId)}`,
    ),

  /**
   * PUT one task's complete assignment set. Replace, not patch — a reassignment
   * lands whole or not at all, so a task can't end up with two drivers. Empty
   * `items` clears it.
   */
  replaceOrderTaskResources: (body: ReplaceOrderTaskResourcesRequest) =>
    request.put<OrderTaskResource[]>('/tms/api/v1/orderTaskResources/task', body),

  /**
   * PUT several tasks' assignments at once, each with its own set — the cascade
   * from a dropped stop to the end of the journey. One transaction.
   */
  replaceTasksResources: (body: ReplaceTasksResourcesRequest) =>
    request.put<OrderTaskResource[]>('/tms/api/v1/orderTaskResources/tasks', body),

  /**
   * PUT one assignment set across every task on an order — the direct-dispatch
   * action, applied in a single transaction. Empty `items` unassigns the order.
   */
  replaceOrderResources: (body: ReplaceOrderResourcesRequest) =>
    request.put<OrderTaskResource[]>('/tms/api/v1/orderTaskResources/order', body),

  /**
   * PUT one assignment set across every task a load touches. Rejected if the load
   * has no cargo yet — there'd be no tasks for it to land on.
   */
  replaceLoadResources: (body: ReplaceLoadResourcesRequest) =>
    request.put<OrderTaskResource[]>('/tms/api/v1/orderTaskResources/load', body),

  /**
   * PUT a whole new task ordering for one order, atomically. Must list
   * EVERY task on the order — a partial payload is rejected. Returns the
   * task list already in its new order, so the caller can seed its cache
   * from the response instead of refetching.
   */
  resequenceOrderTasks: (body: ResequenceOrderTasksRequest) =>
    request.put<OrderTask[]>('/tms/api/v1/orderTasks/resequence', body),

  /**
   * DELETE a task by id. Returns 409 Conflict if the task is a booking
   * half — the FE should hide the trash for those rows so the operator
   * deletes the booking instead.
   */
  deleteOrderTask: (id: string) => request.del<unknown>(`/tms/api/v1/orderTasks/${id}`),

  // ── Order Task Cargo Items ──────────────────────────────────────────────

  /** GET every cargo item on a task, ordered by `itemIndex`. */
  getOrderTaskCargoItems: (orderTaskId: string) =>
    request.get<OrderTaskCargoItem[]>(
      `/tms/api/v1/orderTaskCargoItems?orderTaskId=${encodeURIComponent(orderTaskId)}`,
    ),

  /** POST a new cargo item on a task. Polymorphic via `kind`. */
  createOrderTaskCargoItem: (body: CreateOrderTaskCargoItemRequest) =>
    request.post<OrderTaskCargoItem>('/tms/api/v1/orderTaskCargoItems', body),

  /** PUT an existing cargo item. `orderTaskId` and `kind` are immutable. */
  updateOrderTaskCargoItem: (body: UpdateOrderTaskCargoItemRequest) =>
    request.put<OrderTaskCargoItem>('/tms/api/v1/orderTaskCargoItems', body),

  /** DELETE a cargo item by id. */
  deleteOrderTaskCargoItem: (id: string) =>
    request.del<unknown>(`/tms/api/v1/orderTaskCargoItems/${id}`),

  // ── Order Charge / Cost lines ───────────────────────────────────────────

  /** GET an order's charge/cost lines, ordered by side then sequence. */
  getOrderChargeCosts: (orderId: string) =>
    request.get<OrderChargeCost[]>(
      `/tms/api/v1/orderChargeCosts?orderId=${encodeURIComponent(orderId)}`,
    ),

  /** PUT the full charge/cost set for an order (atomic replace). */
  replaceOrderChargeCosts: (body: ReplaceOrderChargeCostsRequest) =>
    request.put<OrderChargeCost[]>('/tms/api/v1/orderChargeCosts', body),

  // ── Loads (manifests) ───────────────────────────────────────────────────

  /**
   * GET every workflow track's effective policy for an organisation — shipped
   * defaults where nothing has been customised, so the screen never has to
   * decide what a missing track means.
   */
  getWorkflowPolicies: (organisationId: string) =>
    request.get<WorkflowPolicy[]>(`/tms/api/v1/workflowpolicies?organisationId=${organisationId}`),

  /**
   * PUT one track's configuration. Sent whole: a condition list is a set the
   * operator curated, so a partial merge would make removing the last
   * condition impossible to express.
   */
  saveWorkflowPolicy: (organisationId: string, trackKey: string, config: WorkflowPolicyConfig) =>
    request.put<WorkflowPolicy>(
      `/tms/api/v1/workflowpolicies/${trackKey}?organisationId=${organisationId}`,
      config,
    ),

  /**
   * POST a batch of road legs for truck driving times.
   *
   * <p>
   * POST for a read on purpose: the coordinates ARE the query, and a customer's
   * pickup and drop points do not belong in a URL that every proxy and access
   * log between here and the API keeps a copy of. Batched because a journey is
   * several legs at once and one request per leg is three round trips for one
   * screen. A leg that could not be routed comes back with null times, and the
   * caller falls back to its own estimate.
   * </p>
   */

  /** GET loads for a depot/date window, lines embedded. */
  getLoads: (params: {
    organisationId: string;
    depotSiteId?: string | null;
    fromDate?: string | null;
    toDate?: string | null;
  }) => {
    const q = new URLSearchParams({ organisationId: params.organisationId });
    if (params.depotSiteId) q.set('depotSiteId', params.depotSiteId);
    if (params.fromDate) q.set('fromDate', params.fromDate);
    if (params.toDate) q.set('toDate', params.toDate);
    return request.get<Load[]>(`/tms/api/v1/loads?${q.toString()}`);
  },

  /** POST a new load. */
  createLoad: (body: CreateLoadRequest) => request.post<Load>('/tms/api/v1/loads', body),

  /** PUT a load's header. */
  updateLoad: (body: UpdateLoadRequest) => request.put<Load>('/tms/api/v1/loads', body),

  /** DELETE a load (cascades its lines). */
  deleteLoad: (id: string) => request.del<unknown>(`/tms/api/v1/loads/${id}`),

  /** POST lines onto a load. Returns the updated load. */
  assignCargoItemsToLoad: (body: AssignCargoItemsToLoadRequest) =>
    request.post<Load>('/tms/api/v1/loads/assign', body),

  /**
   * PUT a new running order for a load's stops. Must name every stop the load
   * touches; a partial order is rejected. Returns the updated load.
   */
  resequenceLoadStops: (body: ResequenceLoadStopsRequest) =>
    request.put<Load>('/tms/api/v1/loads/resequence-stops', body),

  /** DELETE one load line. Returns the updated load. */
  removeLoadLine: (id: string) => request.del<Load>(`/tms/api/v1/loads/lines/${id}`),

  // ── Order Bookings ──────────────────────────────────────────────────────

  /** GET every booking for an order, with paired tasks embedded as summaries. */
  getOrderBookings: (orderId: string) =>
    request.get<OrderBooking[]>(`/tms/api/v1/orderBookings?orderId=${encodeURIComponent(orderId)}`),

  /** GET one booking by id. */
  getOrderBooking: (id: string) => request.get<OrderBooking>(`/tms/api/v1/orderBookings/${id}`),

  /**
   * POST a new booking. Creates the booking AND its two paired
   * BookingDeparture + BookingArrival tasks atomically in one
   * transaction.
   */
  createOrderBooking: (body: CreateOrderBookingRequest) =>
    request.post<OrderBooking>('/tms/api/v1/orderBookings', body),

  /**
   * PUT an existing booking — booking-level fields only. Per-task edits
   * (port times, addresses) go through `updateOrderTask`.
   */
  updateOrderBooking: (body: UpdateOrderBookingRequest) =>
    request.put<OrderBooking>('/tms/api/v1/orderBookings', body),

  /**
   * DELETE a booking by id. Removes the booking AND its two paired
   * tasks atomically. The FE doesn't need to follow up with task
   * deletes — they're gone in the same transaction.
   */
  deleteOrderBooking: (id: string) => request.del<unknown>(`/tms/api/v1/orderBookings/${id}`),

  /**
   * GET the Bookings cockpit worklist for an organisation over a horizon —
   * committed orders with a task planned in the window, plus booking state.
   * `fromDate` / `toDate` are ISO dates (yyyy-MM-dd); omit for today .. +14 days.
   */
  getBookingWorklist: (organisationId: string, fromDate?: string, toDate?: string) => {
    const params = new URLSearchParams({ organisationId });
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    return request.get<BookingWorklistItem[]>(
      `/tms/api/v1/orderBookings/worklist?${params.toString()}`,
    );
  },

  // ── Order Documents ─────────────────────────────────────────────────────

  /**
   * GET every document for an order. Each DTO carries a short-lived SAS
   * `downloadUrl` so the FE can hand it to `<a href>` / `window.open`
   * without a per-row round-trip.
   */
  getOrderDocuments: (orderId: string) =>
    request.get<OrderDocument[]>(
      `/tms/api/v1/orderDocuments?orderId=${encodeURIComponent(orderId)}`,
    ),

  /** GET one document by id, with a fresh SAS `downloadUrl`. */
  getOrderDocument: (id: string) => request.get<OrderDocument>(`/tms/api/v1/orderDocuments/${id}`),

  /**
   * POST a new document. Multipart/form-data — the helper assembles
   * the form payload from the typed inputs so the caller doesn't have
   * to fiddle with `FormData` directly.
   */
  uploadOrderDocument: (body: UploadOrderDocumentRequest) => {
    const fd = new FormData();
    fd.append('OrderId', body.orderId);
    if (body.orderTaskId) fd.append('OrderTaskId', body.orderTaskId);
    fd.append('Type', body.type);
    if (body.description) fd.append('Description', body.description);
    fd.append('File', body.file);
    return request.post<string>('/tms/api/v1/orderDocuments', fd);
  },

  /**
   * PUT existing document metadata (description / type / task re-link).
   * Replacing the file itself = delete + upload.
   */
  updateOrderDocument: (body: UpdateOrderDocumentRequest) =>
    request.put<string>('/tms/api/v1/orderDocuments', body),

  /** DELETE a document by id. Soft-deletes the row + hard-deletes the blob. */
  deleteOrderDocument: (id: string) => request.del<unknown>(`/tms/api/v1/orderDocuments/${id}`),

  // ── Customer Rate Card ──────────────────────────────────────────────────

  /** GET a customer's full rate card (rate sheets, route sheets, fuel, additionals). */
  getCustomerRateCard: (organisationId: string, customerPartyId: string) =>
    request.get<CustomerRateCard>(
      `/tms/api/v1/customerRates?organisationId=${encodeURIComponent(organisationId)}&customerPartyId=${encodeURIComponent(customerPartyId)}`,
    ),

  /** POST a rate sheet (cargo type → items/UOM with band ladders). Returns the new id. */
  createCustomerRateSheet: (body: CreateCustomerRateSheetRequest) =>
    request.post<string>('/tms/api/v1/customerRates/rate-sheets', body),

  /** POST a route sheet (one lane → included cargo types). Returns the new id. */
  createCustomerRouteSheet: (body: CreateCustomerRouteSheetRequest) =>
    request.post<string>('/tms/api/v1/customerRates/route-sheets', body),

  /** PUT update an existing rate sheet (full replace of the sheet + bands). */
  updateCustomerRateSheet: (body: UpdateCustomerRateSheetRequest) =>
    request.put<unknown>('/tms/api/v1/customerRates/rate-sheets', body),

  /** PUT update an existing route sheet (full replace of the lane + lines). */
  updateCustomerRouteSheet: (body: UpdateCustomerRouteSheetRequest) =>
    request.put<unknown>('/tms/api/v1/customerRates/route-sheets', body),

  /** POST a fuel surcharge (country/region-scoped %). Returns the new id. */
  createCustomerFuelSurcharge: (body: CreateCustomerFuelSurchargeRequest) =>
    request.post<string>('/tms/api/v1/customerRates/fuel-surcharges', body),

  /** PUT update an existing fuel surcharge (rate / territory / effective window). */
  updateCustomerFuelSurcharge: (body: UpdateCustomerFuelSurchargeRequest) =>
    request.put<unknown>('/tms/api/v1/customerRates/fuel-surcharges', body),

  /** POST a per-customer additional charge. Returns the new id. */
  createCustomerAdditionalCharge: (body: CreateCustomerAdditionalChargeRequest) =>
    request.post<string>('/tms/api/v1/customerRates/additionals', body),

  /** DELETE (soft) one rate-card item, discriminated by kind. */
  deleteCustomerRateCardItem: (kind: RateCardItemKindValue, id: string) =>
    request.del<unknown>(`/tms/api/v1/customerRates/${kind}/${id}`),
};

// ────────────────────────────────────────────────────────────────────────────
// Order DTOs + enums
// ────────────────────────────────────────────────────────────────────────────

/**
 * `OrderStatus` — lifecycle states for an order. Values are the BE enum
 * NAMES; keep in lock-step with the C# `My.Tms.Domain.Enums.OrderStatus`.
 *
 * Flow: `Draft → Open → InProgress → Completed`, with `Cancelled`
 * reachable from any non-terminal state.
 *
 * `Open` was `Confirmed` until 2026-09-04, renamed to match the workflow
 * vocabulary — the meaning was always the same. The BE enum keeps its numeric
 * value, so nothing stored moved, but the WIRE STRING changed: an older client
 * sending `Confirmed` is rejected, and this union must stay in lockstep with
 * `My.Tms.Domain.Enums.OrderStatus`.
 */
export type OrderStatusValue = 'Draft' | 'Open' | 'InProgress' | 'Completed' | 'Cancelled';

/**
 * What the Orders screen can sort by — `newest` (created) is the default.
 * `startPoint` is the first task's planned date/time; `nextPoint` the first
 * task still to do, or the last task once every one is done.
 */
export type OrdersSort = 'newest' | 'orderNumber' | 'customer' | 'startPoint' | 'nextPoint';

/** What the Orders screen is asking for. */
export interface OrdersPageParams {
  organisationId: string;
  /** Order number, references 1–4, cargo (and variant) name, customer name or code. */
  search?: string;
  status?: OrderStatusValue | null;
  /**
   * With no `status`: only Draft, Open and In progress — the screen's "All".
   * Ignored when a status is given.
   */
  activeOnly?: boolean;
  /**
   * Inclusive `yyyy-MM-dd` bounds on the order's date (its first task's
   * planned date, else actual). Either may be left open; an order with no
   * dated task is left out once either is set.
   */
  from?: string | null;
  to?: string | null;
  sort?: OrdersSort;
  /** Ignored for `newest`, which is always newest first. */
  descending?: boolean;
}

/** One page of `GET orders/paged`. */
export interface OrdersPage {
  items: Order[];
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Every order matching the search and status, across all pages. */
  totalCount: number;
  /** Every order in the organisation, whatever the search or status. */
  organisationTotal: number;
  /** The organisation's orders per status, whatever the search. */
  statusCounts: { status: OrderStatusValue; count: number }[];
}

/** What the "Start from a previous order" list is asking for. */
export interface PreviousOrdersParams {
  organisationId: string;
  customerPartyId: string;
  /** The cargo picked on the new order, if any — narrows the list to it. */
  cargoDefinitionId?: string | null;
  /** And to one variant of it (e.g. Iso Tank 40ft). Needs `cargoDefinitionId`. */
  cargoDefinitionVariantId?: string | null;
}

/** One page of `GET orders/previous`. */
export interface PreviousOrdersPage {
  items: Order[];
  /** 1-based. */
  page: number;
  pageSize: number;
  /** Every matching order, across all pages. */
  totalCount: number;
}

/**
 * Wire-format projection of `Order`. Carries the picked cargo (def +
 * optional variant), per-order cargo data (temperature, per-trailer
 * unit count denormalised from the variant), four indexed reference
 * slots + a JSON long-tail, free-text notes, and the service tags the
 * operator left active for this shipment.
 *
 * <p>
 * The reference slots are anonymous on the wire — what each slot is
 * *called* in the UI ("Customer PO", "Internal Job") is decided by the
 * org-level `TmsOrderReferenceLabelsJson` on Directory.Organisations.
 * The shape stays generic so the schema doesn't shift when an org
 * renames its labels.
 * </p>
 *
 * <p>
 * `perTrailer` is denormalised from `CargoDefinitionVariant.PerTrailer`
 * at order-entry time (1 or 2). A later catalogue edit of the variant
 * MUST NOT retroactively change live orders' unit counts, hence the
 * snapshot here.
 * </p>
 */
/** One leg of the journey a CO2 estimate was worked out from. */
export interface OrderEmissionLeg {
  /** 'Road' or 'Crossing' (a crossing adds no truck km). */
  kind: string;
  from: string | null;
  to: string | null;
  kilometres: number | null;
  minutes: number | null;
  /** Straight-line estimate: Azure could not route it. */
  estimated: boolean;
  routeName: string | null;
  booked: boolean;
}

/** An order's estimated laden CO2 (Tier 1, Scope 1, tank-to-wheel). */
export interface OrderEmissions {
  orderId: string;
  co2Kg: number;
  ladenKm: number;
  factorKgPerKm: number;
  temperatureControlled: boolean;
  hazardous: boolean;
  /** False when part is a straight-line estimate or a stop has no location: show "~". */
  complete: boolean;
  unlocatedStops: number;
  method: string;
  legs: OrderEmissionLeg[];
  calculatedUtc: string;
  /** Out of date and could not be redone just now; the next read tries again. */
  stale: boolean;
}

export interface OrdersEmissions {
  estimates: OrderEmissions[];
  pendingOrderIds: string[];
}

export interface Order {
  id: string;
  organisationId: string;
  orderNumber: string;
  customerPartyId: string;
  /** Who the charges are invoiced to when not the customer; null = the customer. */
  invoiceToPartyId?: string | null;
  status: OrderStatusValue;
  cargoDefinitionId: string;
  cargoDefinitionVariantId?: string | null;
  /** 1 or 2 — denormalised from the picked variant. Drives the FE's 2-row data strip on per-unit task rows. */
  perTrailer: number;
  /** Single target temperature for this shipment in °C; null when not applicable. */
  temperatureC?: number | null;

  /** Order transactional currency (ISO 4217); null on legacy orders — FE falls back to org default. */
  currencyIsoCode?: string | null;

  /** Reference slot 1 — labelled per organisation. Indexed on the BE. */
  reference1?: string | null;
  /** Reference slot 2 — labelled per organisation. Indexed on the BE. */
  reference2?: string | null;
  /** Reference slot 3 — labelled per organisation. Indexed on the BE. */
  reference3?: string | null;
  /** Reference slot 4 — labelled per organisation. Indexed on the BE. */
  reference4?: string | null;
  /**
   * Long-tail references beyond the four indexed slots, as a JSON
   * object keyed by slot name (e.g. `{ "Reference5": "...", "Reference6": "..." }`).
   * Not indexed — promote anything searchable to one of the four real columns.
   */
  additionalReferencesJson?: string | null;

  /** Free-text order-level notes shown on the Order Details page. */
  notes?: string | null;

  /** Service tags the operator left active on this order. */
  serviceTagIds: string[];
  createdUtc: string;
  updatedUtc?: string | null;
}

/** POST `/tms/api/v1/orders` body. */
export type OrderTemplateStatusValue = 'Active' | 'Archived';

/** A template's stop, as its list draws it. */
export interface OrderTemplateStop {
  sequence: number;
  type: TaskTypeValue;
  addressId: string;
  /** Days after day 0; null for a task with no date. */
  dayOffset: number | null;
  timeMode: TaskTimeModeValue | null;
}

/** A customer's order template — the shape of a job they repeat, without dates. */
export interface OrderTemplate {
  id: string;
  organisationId: string;
  customerPartyId: string;
  name: string;
  description: string | null;
  cargoDefinitionId: string;
  cargoDefinitionVariantId: string | null;
  status: OrderTemplateStatusValue;
  timesUsed: number;
  lastUsedUtc: string | null;
  createdUtc: string;
  serviceTagIds: string[];
  temperatureC: number | null;
  stops: OrderTemplateStop[];
  /** Saved with charge lines — "Template prices" is on offer. */
  hasCharges: boolean;
}

/** A template as an order and its tasks (dates on a fixed day 0 — only the spacing means anything). */
export interface OrderTemplatePreview {
  template: OrderTemplate;
  order: Order;
  tasks: OrderTask[];
}

/** One task to copy, with where it lands on the new order. */
export interface CopyOrderTaskInput {
  sourceTaskId: string;
  /** `yyyy-mm-dd`, or null for no date. */
  plannedDate: string | null;
  timeMode: TaskTimeModeValue;
  /** `HH:mm:ss`, required when `timeMode` is Exact. */
  plannedTime: string | null;
}

/** `POST /orders/copy`. References typed on New order win over copied ones. */
export interface CopyOrderRequest {
  /** The order to copy — or `sourceTemplateId` instead. */
  sourceOrderId?: string | null;
  /** A customer's order template to build the order from. */
  sourceTemplateId?: string | null;
  organisationId: string;
  /** Minted on New order when the customer was picked. */
  orderNumber: string;
  currencyIsoCode: string | null;
  reference1: string | null;
  reference2: string | null;
  reference3: string | null;
  reference4: string | null;
  additionalReferencesJson: string | null;
  includeQuantities: boolean;
  includeTimeWindows: boolean;
  includeTaskNotes: boolean;
  includeBookings: boolean;
  includeReferences: boolean;
  includeDocuments: boolean;
  includeTemperature: boolean;
  status: 'Draft' | 'Open';
  charges: 'Reprice' | 'CopyPrevious';
  /** The tasks to copy, in order; any task not listed is left out. */
  tasks: CopyOrderTaskInput[];
}

export interface CreateOrderRequest {
  organisationId: string;
  /**
   * Pre-allocated reference returned by the Directory sequence consume
   * endpoint. The BE stores it verbatim and 409s on duplicates.
   */
  orderNumber: string;
  customerPartyId: string;
  /** Picked cargo def — required. */
  cargoDefinitionId: string;
  /** Optional — only set for cargo types that expand into variants. */
  cargoDefinitionVariantId?: string | null;
  /**
   * 1 or 2 — snapshot from the picked variant. The FE reads it off the
   * selected `CargoDefinitionVariant.perTrailer` at submit time; default
   * 1 when no variant is picked.
   */
  perTrailer?: number;
  /**
   * Target temperature for this shipment. Required when the picked
   * cargo def has the temperature group ticked; the BE rejects values
   * outside the def's `min/maxTempC` spec with a 422.
   */
  temperatureC?: number | null;

  /** Order transactional currency (ISO 4217) — FE supplies the org default at creation. */
  currencyIsoCode?: string | null;

  // ── Reference slots ──────────────────────────────────────────────────
  reference1?: string | null;
  reference2?: string | null;
  reference3?: string | null;
  reference4?: string | null;
  /** JSON object keyed by slot name beyond Reference4. */
  additionalReferencesJson?: string | null;

  notes?: string | null;

  /** Service tags applied to this order (subset of the cargo def's Orders-area tags). */
  serviceTagIds?: string[];
}

/**
 * PUT `/tms/api/v1/orders` body — a full REPLACE.
 *
 * Every field is required: the handler assigns each column, so one left out of
 * the body is not "unchanged", it is cleared. The server refuses an incomplete
 * body outright; this type makes the same omission a compile error here.
 */
export interface UpdateOrderRequest {
  orderId: string;
  customerPartyId: string;
  status: OrderStatusValue;
  cargoDefinitionId: string;
  cargoDefinitionVariantId: string | null;
  perTrailer: number;
  temperatureC: number | null;

  reference1: string | null;
  reference2: string | null;
  reference3: string | null;
  reference4: string | null;
  additionalReferencesJson: string | null;

  notes: string | null;

  serviceTagIds: string[];
}

// ────────────────────────────────────────────────────────────────────────────
// Order Import DTOs (bulk create — POST /tms/api/v1/orders/import)
// ────────────────────────────────────────────────────────────────────────────

/**
 * One fully-resolved order tree for bulk import. Mirrors `CreateOrderRequest`
 * plus the nested tasks/cargo. Foreign keys (customer, task addresses) are
 * resolved to ids by the FE before sending; `orderNumber` is pre-allocated
 * from the Directory sequence per order. `rowRef` is an opaque FE correlation
 * key echoed back in the result so the UI can map outcomes to source rows.
 */
export interface ImportOrderInput {
  rowRef: string;
  orderNumber: string;
  customerPartyId: string;
  cargoDefinitionId: string;
  cargoDefinitionVariantId?: string | null;
  perTrailer?: number;
  temperatureC?: number | null;
  currencyIsoCode?: string | null;
  reference1?: string | null;
  reference2?: string | null;
  reference3?: string | null;
  reference4?: string | null;
  additionalReferencesJson?: string | null;
  notes?: string | null;
  serviceTagIds?: string[];
  tasks?: ImportOrderTaskInput[];
}

/** One leg of an imported order. Booking-pair types are rejected server-side. */
export interface ImportOrderTaskInput {
  sequence: number;
  type: TaskTypeValue;
  addressId: string;
  taskReference?: string | null;
  notes?: string | null;
  temperatureC?: number | null;
  plannedDate?: string | null;
  plannedTime?: string | null;
  plannedDepartureTime?: string | null;
  timeMode?: TaskTimeModeValue | null;
  serviceTagIds?: string[];
  cargoItems?: ImportCargoItemInput[];
}

/**
 * One cargo item on an imported task. The intra-order collection→delivery
 * allocation is expressed by local refs: a collection item carries a `ref`,
 * the matching delivery item carries that value in `sourceCollectionItemRef`.
 * The server resolves these to real ids within the tree at insert.
 */
export interface ImportCargoItemInput {
  ref?: string | null;
  sourceCollectionItemRef?: string | null;
  itemIndex?: number;
  kind: CargoItemKindValue;
  description?: string | null;
  quantity?: number | null;
  quantityUnit?: string | null;
  weightKg?: number | null;
  volumeM3?: number | null;
  loadingMetresM?: number | null;
  identifier?: string | null;
  seal?: string | null;
  sealStatus?: SealStatusValue | null;
  tankStatus?: TankStatusValue | null;
  capacity?: number | null;
  customsReference?: string | null;
  clearanceStatus?: ClearanceStatusValue | null;
  notes?: string | null;
}

/** POST `/tms/api/v1/orders/import` body. */
export interface ImportOrdersRequest {
  organisationId: string;
  orders: ImportOrderInput[];
}

/** Per-order outcome — `rowRef` echoes the FE correlation key. */
export interface ImportOrderResultItem {
  rowRef: string;
  isCreated: boolean;
  orderId?: string | null;
  orderNumber?: string | null;
  error?: string | null;
}

/**
 * Result of a bulk import. The request succeeds (HTTP 200) whenever it passes
 * authorization + shape validation; individual order failures live in
 * `results`, not as a top-level error — so the UI renders a per-row table and
 * retries only the failures.
 */
export interface ImportOrdersResult {
  results: ImportOrderResultItem[];
  createdCount: number;
  failedCount: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Charge / Cost enums
// ────────────────────────────────────────────────────────────────────────────

/**
 * `ChargeCostType` — classification an operator picks for a charge / cost
 * row. Values are the BE enum NAMES; keep in lock-step with the C#
 * `My.Tms.Domain.Enums.ChargeCostType` enum.
 */
export type ChargeCostTypeValue =
  | 'TransportCharge'
  | 'FuelSurcharge'
  | 'WaitingTime'
  | 'BafCharge'
  | 'AdrCharge'
  | 'DetentionCharge'
  | 'DemurrageCharge'
  | 'HandlingFee'
  | 'StorageFee'
  | 'CustomsClearance'
  | 'Insurance'
  | 'Custom'
  | 'Discount';

/** Display order — matches the BE enum so canonical strings round-trip. */
export const CHARGE_COST_TYPES: readonly ChargeCostTypeValue[] = [
  'TransportCharge',
  'FuelSurcharge',
  'WaitingTime',
  'BafCharge',
  'AdrCharge',
  'DetentionCharge',
  'DemurrageCharge',
  'HandlingFee',
  'StorageFee',
  'CustomsClearance',
  'Insurance',
  'Custom',
  'Discount',
] as const;

/** Operator-facing labels for `ChargeCostTypeValue`. */
const CHARGE_COST_TYPE_LABELS: Record<ChargeCostTypeValue, string> = {
  TransportCharge: 'Transport Charge',
  FuelSurcharge: 'Fuel Surcharge',
  WaitingTime: 'Waiting Time',
  BafCharge: 'BAF Charge',
  AdrCharge: 'ADR Charge',
  DetentionCharge: 'Detention Charge',
  DemurrageCharge: 'Demurrage Charge',
  HandlingFee: 'Handling Fee',
  StorageFee: 'Storage Fee',
  CustomsClearance: 'Customs Clearance',
  Insurance: 'Insurance',
  Custom: 'Custom',
  Discount: 'Discount',
};

/** Which side of the ledger an order charge/cost line sits on. */
export type ChargeCostSideValue = 'Charge' | 'Cost';

export function getChargeCostTypeLabel(t: ChargeCostTypeValue): string {
  return CHARGE_COST_TYPE_LABELS[t] ?? t;
}

/**
 * `ChargeCostAppliesTo` — scope a charge / cost row targets. When
 * `Service`, the row carries a list of `ServiceTag` ids restricted to tags
 * whose `applyToServiceTypes` includes `ChargesAndCosts`.
 */
export type ChargeCostAppliesToValue = 'Orders' | 'Contacts' | 'Service';

export const CHARGE_COST_APPLIES_TO: readonly ChargeCostAppliesToValue[] = [
  'Orders',
  'Contacts',
  'Service',
] as const;

// ────────────────────────────────────────────────────────────────────────────
// Charge / Cost Definition DTOs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Wire-format projection of
 * `My.Tms.Domain.Entities.ChargeCostDefinition`. Service-tag references
 * travel as ids only — the FE has the per-org service-tag list cached
 * anyway and resolves labels client-side. `isSystem` and `isProtected`
 * are reserved for a future seed-row migration; handler-created rows
 * always have both false.
 */
export interface ChargeCostDefinition {
  id: string;
  organisationId: string;
  type: ChargeCostTypeValue;
  displayName: string;
  appliesTo: ChargeCostAppliesToValue;
  salesNominal: string | null;
  purchaseNominal: string | null;
  /** This charge's accounting category CODE; null = none (yet). */
  accountingCategoryCode: string | null;
  /**
   * On: a line takes the product's category for that cargo, else the cargo
   * definition's, else this charge's. Off: always this charge's.
   */
  categoryFollowsJob: boolean;
  /**
   * This charge's VAT rate (Directory tax schema id), used only when the VAT
   * treatment lands on the standard line. Null = the treatment's schema.
   */
  taxRateId: string | null;
  /**
   * "VAT follows the job" — separate from the category switch. On: the
   * product's, then the cargo's rate win over `taxRateId`. Off: always this
   * charge's. On by default for every charge.
   */
  vatFollowsJob: boolean;
  isSystem: boolean;
  isProtected: boolean;
  status: BaseStatusValue;
  /** Empty when `appliesTo` is not `Service`. */
  serviceTagIds: string[];
  createdUtc: string;
  updatedUtc: string | null;
}

/** POST `/tms/api/v1/chargeCostDefinitions` body. */
export interface CreateChargeCostDefinitionRequest {
  organisationId: string;
  type: ChargeCostTypeValue;
  displayName: string;
  appliesTo: ChargeCostAppliesToValue;
  salesNominal?: string | null;
  purchaseNominal?: string | null;
  status?: BaseStatusValue;
  /** Only meaningful when `appliesTo === 'Service'`. Empty otherwise. */
  serviceTagIds?: string[];
  accountingCategoryCode?: string | null;
  /** Left out → on for Transport Charge and Fuel Surcharge, off for the rest. */
  categoryFollowsJob?: boolean;
  taxRateId?: string | null;
  /** Left out → on. */
  vatFollowsJob?: boolean;
}

/**
 * PUT `/tms/api/v1/chargeCostDefinitions` body. The BE rejects:
 *  - `type` changes on `isSystem` rows
 *  - `appliesTo` changes on `isProtected` rows
 * The FE locks the corresponding controls but the BE enforces both as
 * defence in depth.
 */
export interface UpdateChargeCostDefinitionRequest {
  chargeCostDefinitionId: string;
  type: ChargeCostTypeValue;
  displayName: string;
  appliesTo: ChargeCostAppliesToValue;
  salesNominal?: string | null;
  purchaseNominal?: string | null;
  status?: BaseStatusValue;
  serviceTagIds?: string[];
  /** Applied only when sent — an editor that leaves them out keeps the stored values. */
  accountingCategoryCode?: string | null;
  categoryFollowsJob?: boolean;
  /** Applied only when sent; explicit null clears. */
  taxRateId?: string | null;
  /** Applied only when sent. */
  vatFollowsJob?: boolean;
}

// ────────────────────────────────────────────────────────────────────────────
// Order Charge / Cost DTOs
// ────────────────────────────────────────────────────────────────────────────

/**
 * One commercial line on an order — a Charge (billed to the customer) or a
 * Cost (paid to a supplier), per `side`. NET only at this level; nominals /
 * VAT are an invoicing concern. `description`, `type` and `isProtected` are
 * snapshots from the catalogue definition (when `chargeCostDefinitionId` is
 * set) so renaming/retiring a definition never mutates a priced order.
 */
export interface OrderChargeCost {
  id: string;
  orderId: string;
  side: ChargeCostSideValue;
  /** Soft link to the catalogue definition; null for an ad-hoc line. */
  chargeCostDefinitionId?: string | null;
  description: string;
  type: ChargeCostTypeValue;
  net: number;
  sequence: number;
  /** Snapshot of the definition flag — protected lines can't be removed. */
  isProtected: boolean;
  /** Percentage rate for a derived line (fuel surcharge / %-of-base); null otherwise. */
  rate: number | null;
  /** True when `net` is computed (rate × base) and not directly editable. */
  isLocked: boolean;
  /** True when this line feeds the fuel-surcharge base. */
  fsApplicable: boolean;
  /** Set once this line has been pulled into an invoice (later). */
  invoiceId?: string | null;
  /** Charge side: who pays this charge when not the order's payer; null = the order's. */
  billToPartyId?: string | null;
}

/** One desired line in a {@link ReplaceOrderChargeCostsRequest}. */
export interface OrderChargeCostLineInput {
  /** Existing row id; omit for a new line. */
  id?: string | null;
  side: ChargeCostSideValue;
  /** Catalogue line — server snapshots description/type/protected from it. */
  chargeCostDefinitionId?: string | null;
  /** Ad-hoc lines only (catalogue lines snapshot from the definition). */
  description?: string | null;
  /** Ad-hoc lines only (Custom / Discount); defaults to Custom. */
  type?: ChargeCostTypeValue | null;
  net: number;
  sequence: number;
  /** Percentage rate for a derived line (fuel surcharge / %-of-base); null otherwise. */
  rate?: number | null;
  /** True when `net` is computed (rate × base) — recomputed server-side on save. */
  isLocked?: boolean;
  /** True when this line feeds the fuel-surcharge base. */
  fsApplicable?: boolean;
}

/** PUT `/tms/api/v1/orderChargeCosts` body — atomic replace of the full set. */
export interface ReplaceOrderChargeCostsRequest {
  orderId: string;
  lines: OrderChargeCostLineInput[];
}

// ── Invoice approval (Finance → Invoicing "For approval" tab) ───────────────

/** `ignore | warn | stop` — mirrors My.Tms `ApprovalSeverity`. Ignore-level
 * checks are filtered out server-side, so a returned exception is Warn or Stop. */
export type ApprovalSeverityValue = 'Ignore' | 'Warn' | 'Stop';

/** A surfaced approval issue on an order. For reference checks `referenceSlot`
 * (1–4) lets the FE label it with the org's custom reference name. */
export interface ApprovalException {
  /** `pod` | `charges` | `reference1`…`reference4`. */
  code: string;
  severity: ApprovalSeverityValue;
  referenceSlot?: number | null;
}

/** A row on the "For approval" screen — an order Completed + Pending invoicing.
 * `customerPartyId` and the address ids are resolved to display text by the FE. */
export interface OrderForApproval {
  id: string;
  orderNumber: string;
  customerPartyId: string;
  currencyIsoCode?: string | null;
  collectionAddressId?: string | null;
  deliveryAddressId?: string | null;
  /** Actual collected / delivered dates (ISO `yyyy-MM-dd`) or null. */
  collectedDate?: string | null;
  deliveredDate?: string | null;
  cargo?: string | null;
  reference1?: string | null;
  reference2?: string | null;
  reference3?: string | null;
  reference4?: string | null;
  transport: number;
  fuel: number;
  other: number;
  total: number;
  exceptions: ApprovalException[];
  /** True when nothing stop-level is outstanding (warnings are fine). */
  canApprove: boolean;
  /** Non-null once previously approved (e.g. bounced back to Pending). */
  approvedForInvoicingUtc?: string | null;
}

/** POST `/tms/api/v1/orders/approve-for-invoicing` body. */
export interface ApproveOrdersForInvoicingRequest {
  organisationId: string;
  orderIds: string[];
}

/** Per-order outcome of an approve call. */
export interface ApproveOrdersForInvoicingResult {
  approved: string[];
  rejected: { orderId: string; reason: string }[];
}

// ── Invoices (Create + Summary tabs) ────────────────────────────────────────

export type InvoiceStatusValue = 'Draft' | 'Issued' | 'Sent' | 'Paid' | 'Cancelled';
export type InvoiceDocTypeValue = 'Standard' | 'Proforma' | 'Credit' | 'Adhoc';
export type InvoiceExportStatusValue = 'Pending' | 'InProgress' | 'Exported';

/** POST `/invoices/{id}/cancel` body. */
export interface CancelInvoiceRequest {
  /** Why it is being voided — required, and kept on the invoice. */
  reason: string;
  /**
   * Set once the operator confirms the invoice has ALSO been cancelled in the
   * accounting system. Only needed when it was already exported there; the
   * server refuses without it rather than letting the two drift apart.
   */
  accountingCancellationAcknowledged: boolean;
}
/** VAT rounding policy — mirrors the org setting. `Item` = per line; `Total` = per rate band on the document. */
export type VatRoundingMethodValue = 'Item' | 'Total';

/** A Create-invoices row — an Approved order with its remaining billable charges. */
export interface OrderForInvoicing {
  id: string;
  orderNumber: string;
  customerPartyId: string;
  currencyIsoCode?: string | null;
  /**
   * Lifecycle status, so the screen can apply the workflow policy's "Appears in
   * invoicing from …" rule. That decision needs the customer's payment terms,
   * which live on the party in Directory — the TMS service holds only a soft
   * pointer, so it cannot make the call itself.
   */
  status: OrderStatusValue;
  collectionAddressId?: string | null;
  deliveryAddressId?: string | null;
  /** Actual dates — null until the task is marked collected / delivered. */
  collectedDate?: string | null;
  deliveredDate?: string | null;
  /**
   * Planned task dates. An order can be billed before its tasks are marked
   * complete, so the invoice falls back to these rather than printing a blank
   * date. The list columns still show the actuals only.
   */
  plannedCollectionDate?: string | null;
  plannedDeliveryDate?: string | null;
  cargo?: string | null;
  /** Who the order's charges go to when not the customer; a charge's billToPartyId wins. */
  invoiceToPartyId?: string | null;
  /** The order's cargo type — for a cargo-level VAT rate on the standard line. */
  cargoDefinitionId: string;
  /** Directory products loaded on the order (distinct) — for product-level VAT rates. */
  productIds: string[];
  /** The same products as loaded (the day's copy), in loading order, for printing. */
  products: OrderInvoiceProduct[];
  /**
   * Trailer and container as recorded on the order's tasks — snapshots taken at
   * assignment time, so they do not change if the unit is re-registered later.
   */
  trailer?: string | null;
  container?: string | null;
  reference1?: string | null;
  reference2?: string | null;
  reference3?: string | null;
  reference4?: string | null;
  /** Un-invoiced Charge-side lines available to bill. */
  charges: OrderChargeCost[];
}

/** A product loaded on an order, as the invoice prints it. */
export interface OrderInvoiceProduct {
  productId: string;
  name: string;
  unNumber?: string | null;
}

export interface InvoiceLine {
  id: string;
  orderId?: string | null;
  orderNumber?: string | null;
  sourceChargeCostId?: string | null;
  /** Credit-note line: the original line it credits. */
  creditsInvoiceLineId?: string | null;
  /** Invoice line: net already credited by live credit notes (what's left = net − this). */
  creditedNet?: number;
  description: string;
  type: ChargeCostTypeValue;
  salesNominal?: string | null;
  net: number;
  taxRatePct: number;
  taxAmount: number;
  /** Directory tax schema, as it stood when the invoice was raised (server-copied). */
  taxRateId?: string | null;
  taxRateName?: string | null;
  /** Standard / Zero / Exempt / IntraEu / ReverseCharge / NoVat. */
  taxType?: string | null;
  /** The schema's "Description on invoice" — printed on the VAT band. */
  taxDescription?: string | null;
  /** The VAT treatment line (`EU-3`). */
  vatTreatmentKey?: string | null;
  accountingCategoryCode?: string | null;
  /** The goods carried, as printed. */
  products?: string | null;
  /** Hand changes from the proposed default — `{"Vat":{"from":…,"to":…}}`; null when none. */
  overridesJson?: string | null;
  sequence: number;
}

export interface Invoice {
  id: string;
  number: string;
  docType: InvoiceDocTypeValue;
  status: InvoiceStatusValue;
  customerPartyId: string;
  customerName: string;
  customerCode?: string | null;
  billingName?: string | null;
  billingAddress?: string | null;
  /** The customer's VAT number and VAT country as they stood when raised. */
  customerTaxNumber?: string | null;
  customerTaxCountry?: string | null;
  currencyIsoCode?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  /** Payment terms as worded when the invoice was issued (a snapshot). */
  paymentTerms?: string | null;
  poNumber?: string | null;
  /** Printed on the document — the thank-you line under the totals. */
  message?: string | null;
  /** Covering note for the email that sends it. Never printed. */
  emailMessage?: string | null;
  subtotal: number;
  vatTotal: number;
  total: number;
  issuedUtc?: string | null;
  sentUtc?: string | null;
  /** Who the most recent email went to, comma-separated. */
  sentTo?: string | null;
  paidUtc?: string | null;
  cancelledUtc?: string | null;
  cancellationReason?: string | null;
  exportStatus: InvoiceExportStatusValue;
  exportedUtc?: string | null;
  exportReference?: string | null;
  /**
   * When the issued PDF was stored. Null means nothing was ever uploaded, so a
   * download has to render the document instead of serving it.
   */
  pdfGeneratedUtc?: string | null;
  /** Derived: issued/sent + unpaid past its due date. */
  isOverdue: boolean;
  overdueDays?: number | null;
  /** On a credit note: the invoice it credits (printed "Credit note for invoice …"). */
  creditNoteOfInvoiceId?: string | null;
  creditNoteOfNumber?: string | null;
  creditNoteOfIssueDate?: string | null;
  creditReason?: string | null;
  /** The credit note handed its charges back for re-invoicing — it cannot be cancelled. */
  releasedCharges?: boolean;
  /** On an invoice: total of its live credit notes. */
  creditedTotal?: number;
  /** On an invoice: its credit notes, cancelled ones included. */
  creditNotes?: CreditNoteRef[];
  lines: InvoiceLine[];
}

export interface CreditNoteRef {
  id: string;
  number: string;
  status: InvoiceStatusValue;
  issueDate?: string | null;
  total: number;
  reason?: string | null;
}

/** PUT `/tms/api/v1/invoices/{id}` body — edits a DRAFT (or proforma). `lines` is the whole new list. */
export interface UpdateDraftInvoiceRequest {
  issueDate?: string | null;
  dueDate?: string | null;
  poNumber?: string | null;
  message?: string | null;
  emailMessage?: string | null;
  roundingMethod?: VatRoundingMethodValue;
  lines: UpdateDraftInvoiceLineInput[];
}

export interface UpdateDraftInvoiceLineInput {
  /** An existing line; omit for a new one. Lines left out are removed (charges released). */
  id?: string | null;
  /** New sourced line: the un-invoiced charge it bills. */
  sourceChargeCostId?: string | null;
  /** Ad-hoc lines only. */
  description?: string | null;
  salesNominal?: string | null;
  accountingCategoryCode?: string | null;
  vatTreatmentKey?: string | null;
  products?: string | null;
  net: number;
  taxRatePct: number;
  taxRateId?: string | null;
  /** Hand changes in this edit, each from its value before the edit. */
  overrides?: InvoiceLineOverride[];
}

/** POST `/tms/api/v1/invoices/{id}/credit-note` body. */
export interface RaiseCreditNoteRequest {
  /** Pre-minted from the organisation's CreditNote sequence. */
  number: string;
  /** Required — printed on the credit note. */
  reason: string;
  issueDate?: string | null;
  issueNow?: boolean;
  /** Hand fully credited lines' charges back for re-invoicing. */
  releaseCharges?: boolean;
  roundingMethod?: VatRoundingMethodValue;
  lines: { invoiceLineId: string; net: number }[];
}

/** A row on the Invoice-summary tab — customer fields are the invoice's snapshot. */
export interface InvoiceSummary {
  id: string;
  number: string;
  docType: InvoiceDocTypeValue;
  status: InvoiceStatusValue;
  isOverdue: boolean;
  overdueDays?: number | null;
  customerPartyId: string;
  customerName: string;
  customerCode?: string | null;
  currencyIsoCode?: string | null;
  /** Covered order numbers, or "—" for ad-hoc. */
  orders: string;
  subtotal: number;
  vatTotal: number;
  total: number;
  issueDate?: string | null;
  dueDate?: string | null;
  exportStatus: InvoiceExportStatusValue;
  /**
   * When the issued PDF was stored. Null means nothing was ever uploaded, so a
   * download has to render the document instead of serving it.
   */
  pdfGeneratedUtc?: string | null;
  /** On a credit note: the invoice it credits. */
  creditNoteOfInvoiceId?: string | null;
  creditNoteOfNumber?: string | null;
  /** On an invoice: total of its live credit notes, and their numbers. */
  creditedTotal?: number;
  creditNoteNumbers?: string | null;
}

/** One line in a {@link GenerateInvoiceRequest}. */
export interface GenerateInvoiceLineInput {
  /** Order charge being billed; omit for an ad-hoc line. */
  sourceChargeCostId?: string | null;
  orderId?: string | null;
  orderNumber?: string | null;
  /** Ad-hoc lines only — sourced lines snapshot description/type server-side. */
  description?: string | null;
  type?: ChargeCostTypeValue | null;
  /** Optional sales nominal — overrides the catalogue default (set it for ad-hoc lines). */
  salesNominal?: string | null;
  net: number;
  /** With `taxRateId` the server takes the schema's own rate; this only matters without one. */
  taxRatePct: number;
  taxRateId?: string | null;
  /** The VAT treatment line the builder resolved (`EU-1`). */
  vatTreatmentKey?: string | null;
  accountingCategoryCode?: string | null;
  /** The goods carried, as the line prints them. */
  products?: string | null;
  /** Fields changed by hand — logged as "Overridden" and kept on the line. */
  overrides?: InvoiceLineOverride[];
}

/** One hand change on an invoice line: what the builder proposed and what it became. */
export interface InvoiceLineOverride {
  field: 'Vat' | 'Nominal' | 'Category';
  from: string | null;
  to: string | null;
}

/** POST `/tms/api/v1/invoices` body. `number` is pre-minted via the org sequence. */
export interface GenerateInvoiceRequest {
  /**
   * The customer's billing treatment (`Cash` | `Credit` | `SelfBill` |
   * `Internal`). Sent because the server enforces the same "Appears in
   * invoicing from …" rule and cannot read the party itself.
   */
  customerPaymentMethod?: string | null;
  organisationId: string;
  number: string;
  docType: InvoiceDocTypeValue;
  customerPartyId: string;
  customerName: string;
  customerCode?: string | null;
  billingName?: string | null;
  billingAddress?: string | null;
  customerTaxNumber?: string | null;
  customerTaxCountry?: string | null;
  currencyIsoCode?: string | null;
  issueDate?: string | null;
  dueDate?: string | null;
  /**
   * Payment terms wording to snapshot onto the invoice, derived by the caller
   * from the customer's terms or the organisation default. The server stores
   * it verbatim.
   */
  paymentTerms?: string | null;
  poNumber?: string | null;
  /** Printed on the document. */
  message?: string | null;
  /** Covering note for the email. Stored, never printed. */
  emailMessage?: string | null;
  /** True → Issued immediately (send); false → Draft (draft / proforma). */
  issueNow: boolean;
  /** VAT rounding policy from the org settings; defaults to Total server-side. */
  roundingMethod?: VatRoundingMethodValue;
  lines: GenerateInvoiceLineInput[];
}

// ────────────────────────────────────────────────────────────────────────────
// Order Task / Cargo Item / Booking / Document enums
// ────────────────────────────────────────────────────────────────────────────

/**
 * `TaskType` — the kind of leg an order task represents. Mirrors the C#
 * `My.Tms.Domain.Enums.TaskType`. `Booking` is split into two halves
 * (`BookingDeparture` + `BookingArrival`); everything else maps 1:1 to
 * a single task row.
 */
export type TaskTypeValue =
  | 'BookingDeparture'
  | 'BookingArrival'
  | 'Instruction'
  | 'Stop'
  | 'Collect'
  | 'Deliver'
  | 'PickUp'
  | 'DropOff'
  | 'Load'
  | 'Unload'
  | 'Inspection'
  | 'TempCheck'
  | 'ReeferInspection'
  | 'SampleCheck'
  | 'Weigh'
  | 'Wash'
  | 'TankClean'
  | 'VetCheck'
  | 'BorderInspection'
  | 'PhytosanitaryCheck'
  | 'FumigationStop'
  | 'PermitStop'
  | 'SiteCheck'
  | 'EscortStart'
  | 'FeedWaterStop';

/** `TaskState` — execution lifecycle of a task. */
export type TaskStateValue = 'Pending' | 'InProgress' | 'Completed' | 'Cancelled';

/**
 * `TaskTimeMode` — how precise a Pickup / Delivery task's planned time
 * is. Null on booking halves (they always have specific sail/dock times).
 */
export type TaskTimeModeValue = 'Any' | 'AM' | 'PM' | 'Exact';

/** `CargoItemKind` — what kind of "thing handled" a cargo-item row represents. */
export type CargoItemKindValue =
  | 'ContainerUnit'
  | 'TankUnit'
  | 'PalletLine'
  | 'BulkBatch'
  | 'LivestockBatch'
  | 'Other';

/** `SealStatus` — state of a container/tank seal at delivery / unload. */
export type SealStatusValue = 'Intact' | 'Broken' | 'Replaced';

/** `TankStatus` — cleanliness/fill state of an iso-tank at a task. */
export type TankStatusValue = 'EmptyClean' | 'EmptyDirty' | 'Full';

/** `PodStatus` — proof-of-delivery lifecycle. */
export type PodStatusValue = 'Required' | 'Pending' | 'Signed';

/** `ClearanceStatus` — customs clearance state. */
export type ClearanceStatusValue = 'Required' | 'Pending' | 'Cleared';

/**
 * `BookingType` — vehicle / cargo category a ferry slot is reserved
 * under. Carriers price slots by this category.
 */
export type BookingTypeValue =
  | 'Artic'
  | 'LorryOver6t'
  | 'RoadTrain'
  | 'SoloTractor'
  | 'Trailer'
  | 'VanUnder6t'
  | 'Container20to29'
  | 'Container30to39'
  | 'Container40to49'
  | 'Driver';

/** `OrderDocumentType` — classification of a file uploaded against an order. */
export type OrderDocumentTypeValue =
  | 'Pod'
  | 'Cmr'
  | 'TransportOrder'
  | 'Invoice'
  | 'Manifest'
  | 'CustomsDeclaration'
  | 'Bookings'
  | 'Photo'
  | 'Other';

// ────────────────────────────────────────────────────────────────────────────
// Order Task DTOs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Wire-format projection of `OrderTask`. Embeds `cargoItems` so a list
 * endpoint returns the full per-task data strip in one round-trip.
 */
export interface OrderTask {
  id: string;
  organisationId: string;
  orderId: string;
  sequence: number;
  type: TaskTypeValue;
  addressId: string;
  /** Set on the two crossing halves (`BookingDeparture` / `BookingArrival`); null otherwise. */
  bookingId?: string | null;
  taskReference?: string | null;
  notes?: string | null;
  /** Per-task target temperature (°C) for temperature-controlled cargo. Null otherwise. */
  temperatureC?: number | null;

  // Planned
  plannedDate?: string | null; // ISO `yyyy-MM-dd`
  plannedTime?: string | null; // ISO `HH:mm:ss`
  plannedDepartureTime?: string | null;
  timeMode?: TaskTimeModeValue | null;

  // Actual
  actualDate?: string | null;
  actualArrivalTime?: string | null;
  actualDepartureTime?: string | null;

  // State
  state: TaskStateValue;
  statusText?: string | null;

  // Per-task POD
  podStatus?: PodStatusValue | null;
  podSignedAt?: string | null; // ISO instant
  podSignedBy?: string | null;

  cargoItems: OrderTaskCargoItem[];

  /**
   * Service tag ids applied to this specific task. Independent of
   * the order-level set on `Order.serviceTagIds` — the per-task
   * selection is seeded from the order's tags at create time and
   * can be narrowed / widened from there. Drives the per-task
   * data-strip Tags chip row.
   */
  serviceTagIds: string[];

  createdUtc: string;
  updatedUtc?: string | null;
}

/** POST `/tms/api/v1/orderTasks` body. */
export interface CreateOrderTaskRequest {
  orderId: string;
  sequence: number;
  type: TaskTypeValue;
  addressId: string;
  taskReference?: string | null;
  notes?: string | null;
  /** Per-task target temperature (°C) for temperature-controlled cargo. Null otherwise. */
  temperatureC?: number | null;
  plannedDate?: string | null;
  plannedTime?: string | null;
  plannedDepartureTime?: string | null;
  timeMode?: TaskTimeModeValue | null;
  state?: TaskStateValue;
  statusText?: string | null;
  /** Per-task service tag ids. Empty array = no tags on this task. */
  serviceTagIds?: string[];
}

/**
 * PUT `/tms/api/v1/orderTasks` body — a full REPLACE.
 *
 * Every field is required. A panel that edits two of them (the booking editor
 * edits the ports and the sailing) has to echo the rest off the loaded task —
 * hard-coding nulls there cleared each leg's temperature, notes, reference, POD
 * and service tags on every booking save.
 */
export interface UpdateOrderTaskRequest {
  taskId: string;
  sequence: number;
  addressId: string;
  taskReference: string | null;
  notes: string | null;
  /** Per-task target temperature (°C) for temperature-controlled cargo. Null otherwise. */
  temperatureC: number | null;
  plannedDate: string | null;
  plannedTime: string | null;
  plannedDepartureTime: string | null;
  timeMode: TaskTimeModeValue | null;
  actualDate: string | null;
  actualArrivalTime: string | null;
  actualDepartureTime: string | null;
  state: TaskStateValue;
  statusText: string | null;
  podStatus: PodStatusValue | null;
  podSignedAt: string | null;
  podSignedBy: string | null;
  /** Diff-replaces the task's per-task service tag set on save. Empty array = clear. */
  serviceTagIds: string[];
}

/**
 * PUT `/tms/api/v1/orderTasks/resequence` body. `items` must cover every
 * task on the order; sequences must be distinct (gaps are allowed).
 */
export interface ResequenceOrderTasksRequest {
  orderId: string;
  items: OrderTaskSequenceInput[];
}

/** One task's new position within its order. */
export interface OrderTaskSequenceInput {
  taskId: string;
  sequence: number;
}

// ────────────────────────────────────────────────────────────────────────────
// Order Task Cargo Item DTOs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Polymorphic via `kind`. Container/tank rows fill `identifier` + `seal`;
 * pallet lines fill `quantity` + `quantityUnit` + `description`; bulk
 * batches fill weight + volume only.
 */
export interface OrderTaskCargoItem {
  id: string;
  organisationId: string;
  orderTaskId: string;
  itemIndex: number;
  kind: CargoItemKindValue;
  /** Collection cargo item this delivery item draws from (delivery legs only). */
  sourceCollectionItemId?: string | null;

  // Common
  description?: string | null;
  quantity?: number | null;
  quantityUnit?: string | null;
  weightKg?: number | null;
  volumeM3?: number | null;
  /** Loading metres this line occupies (derived for pallets, manual otherwise). */
  loadingMetresM?: number | null;

  // Container / Tank
  identifier?: string | null;
  seal?: string | null;
  sealStatus?: SealStatusValue | null;
  tankStatus?: TankStatusValue | null;
  capacity?: number | null;

  // Customs / Clearance (per-item)
  customsReference?: string | null;
  clearanceStatus?: ClearanceStatusValue | null;

  // Per-item POD
  podStatus?: PodStatusValue | null;
  podSignedAt?: string | null;
  podSignedBy?: string | null;

  notes?: string | null;

  /** For a delivery drawn from one line of a multi-product unit: which line. */
  sourceProductLineId?: string | null;

  /** What the unit carries, in compartment order (`sequence`). */
  products?: OrderTaskCargoItemProduct[];

  createdUtc: string;
  updatedUtc?: string | null;
}

/** One product in a cargo unit — the server's copy of the Directory product. */
export interface OrderTaskCargoItemProduct {
  id: string;
  sequence: number;
  /** Optional with one line (the unit's weight stands); summed into the unit with several. */
  weightKg: number | null;
  volumeM3: number | null;
  productId: string;
  productName: string;
  productUnNumber: string | null;
  productShippingName: string | null;
  productTechnicalName: string | null;
  productAdrClass: string | null;
  productPackingGroup: string | null;
  productHazardNumber: string | null;
  productTunnelCode: string | null;
  productEwcCode: string | null;
  productEwcHazardous: boolean | null;
  productGrade: string | null;
}

/**
 * One product line on a cargo-row save. The list order is the compartment
 * order; `id` keeps an existing line (and its copy) through a move.
 */
export interface CargoItemProductInput {
  id?: string | null;
  productId: string;
  weightKg?: number | null;
  volumeM3?: number | null;
  /**
   * Which of the product's waste codes this consignment declares. Null keeps
   * the line's code; a new or changed product takes the product's default.
   */
  ewcCode?: string | null;
}

/** POST `/tms/api/v1/orderTaskCargoItems` body. */
export interface CreateOrderTaskCargoItemRequest {
  orderTaskId: string;
  itemIndex?: number;
  kind: CargoItemKindValue;
  sourceCollectionItemId?: string | null;
  description?: string | null;
  quantity?: number | null;
  quantityUnit?: string | null;
  weightKg?: number | null;
  volumeM3?: number | null;
  loadingMetresM?: number | null;
  identifier?: string | null;
  seal?: string | null;
  sealStatus?: SealStatusValue | null;
  tankStatus?: TankStatusValue | null;
  capacity?: number | null;
  customsReference?: string | null;
  clearanceStatus?: ClearanceStatusValue | null;
  podStatus?: PodStatusValue | null;
  podSignedAt?: string | null;
  podSignedBy?: string | null;
  notes?: string | null;
  sourceProductLineId?: string | null;
  /** What the unit carries, in order — ids only; the server copies each product. */
  products?: CargoItemProductInput[];
}

/**
 * PUT `/tms/api/v1/orderTaskCargoItems` body — a full REPLACE.
 *
 * Every field is required; build it with `cargoItemUpdateBody(item, patch)`,
 * which echoes all of them off the row and overrides only what the caller owns.
 */
export interface UpdateOrderTaskCargoItemRequest {
  cargoItemId: string;
  itemIndex: number;
  sourceCollectionItemId: string | null;
  description: string | null;
  quantity: number | null;
  quantityUnit: string | null;
  weightKg: number | null;
  volumeM3: number | null;
  loadingMetresM: number | null;
  identifier: string | null;
  seal: string | null;
  sealStatus: SealStatusValue | null;
  tankStatus: TankStatusValue | null;
  capacity: number | null;
  customsReference: string | null;
  clearanceStatus: ClearanceStatusValue | null;
  podStatus: PodStatusValue | null;
  podSignedAt: string | null;
  podSignedBy: string | null;
  notes: string | null;
  sourceProductLineId: string | null;
  /**
   * The WHOLE list, in order. Lines sent with their id keep their copy; new
   * lines and changed products are read from Directory; lines left out go.
   */
  products: CargoItemProductInput[];
}

// ────────────────────────────────────────────────────────────────────────────
// Workflow policies (Settings → Workflows)
// ────────────────────────────────────────────────────────────────────────────

/** One condition on a move, or on the cancellation policy. */
export interface WorkflowRequirementConfig {
  /** Catalogue key, e.g. `customerReference`. */
  kind: string;
  severity: 'ignore' | 'warn' | 'stop';
  /** `require` (default) or `prevent`. */
  polarity?: 'require' | 'prevent' | null;
  param?: string | null;
}

export interface WorkflowStateBehaviourConfig {
  plannable: boolean;
  editable: boolean;
  /** Credit customers only; absent on non-order tracks. */
  invoiceable?: boolean | null;
}

/**
 * What an organisation can decide about a track — OVERRIDES only. The states a
 * track has and the moves between them are code (they map to the status enums),
 * so they are not stored; a key naming a state or move this build no longer has
 * is simply ignored when applied.
 */
export interface WorkflowPolicyConfig {
  /** Keyed by state key, e.g. `open`. */
  states: Record<string, WorkflowStateBehaviourConfig>;
  /** Keyed `"{from}->{to}"` — a transition has no id of its own. */
  transitions: Record<string, WorkflowRequirementConfig[]>;
  cancelRequirements: WorkflowRequirementConfig[];
}

export interface WorkflowPolicy {
  /** `order` | `trip` | `leg`. */
  trackKey: string;
  /**
   * False when nothing is stored and the shipped default is being shown. The
   * difference between "an operator chose this" and "nobody has looked at it
   * yet" — which matters when the default later improves.
   */
  isCustomised: boolean;
  updatedUtc?: string | null;
  updatedBy?: string | null;
  config: WorkflowPolicyConfig;
}

// ────────────────────────────────────────────────────────────────────────────
// Load (manifest) DTOs
// ────────────────────────────────────────────────────────────────────────────

export type LoadStatusValue = 'Draft' | 'Planned' | 'Dispatched' | 'Completed' | 'Cancelled';

/**
 * `AssignmentSlot` — what kind of thing fills an assignment position on a task.
 * Named "slot" rather than "role" because `LoadLineLegRole` already uses role for
 * the collection/delivery/trunk axis, and half of these are equipment.
 * `Vehicle` covers both Truck and Rigid.
 */
export type AssignmentSlotValue = 'Driver' | 'CoDriver' | 'Vehicle' | 'Trailer' | 'Container';

/**
 * Who or what is doing a task. Hangs off the task rather than the load, because
 * plenty of customers dispatch orders without ever building a manifest — and it
 * covers tasks that have no cargo items at all (ferry halves, process events).
 */
export interface OrderTaskResource {
  id: string;
  orderTaskId: string;
  /** Set only on split work — null means the assignment covers the whole task. */
  orderTaskCargoItemId?: string | null;
  slot: AssignmentSlotValue;
  slotIndex: number;
  /** Per-slot: our driver can pull a subcontractor's trailer, and vice versa. */
  isSubcontractor: boolean;
  /** Our resource (Directory). Set when `isSubcontractor` is false. */
  resourceId?: string | null;
  /** Supplying haulier (Directory party). Set when `isSubcontractor` is true. */
  subcontractorPartyId?: string | null;
  /** A registered subcontractor trailer (Directory). */
  subcontractorTrailerId?: string | null;
  /** Free-text reg / name for an unregistered subcontracted unit. */
  externalReference?: string | null;
  /** What the resource was called at assignment time, so history doesn't shift. */
  referenceSnapshot?: string | null;
  createdUtc: string;
  updatedUtc?: string | null;
}

/** One slot in a replace payload. */
export interface OrderTaskResourceInput {
  slot: AssignmentSlotValue;
  slotIndex?: number;
  isSubcontractor: boolean;
  resourceId?: string | null;
  subcontractorPartyId?: string | null;
  subcontractorTrailerId?: string | null;
  externalReference?: string | null;
  referenceSnapshot?: string | null;
  /** Per-task endpoint only — an item belongs to exactly one task. */
  orderTaskCargoItemId?: string | null;
}

/** PUT `/tms/api/v1/orderTaskResources/task` body. Empty `items` clears the task. */
export interface ReplaceOrderTaskResourcesRequest {
  orderTaskId: string;
  items: OrderTaskResourceInput[];
}

/**
 * PUT `/tms/api/v1/orderTaskResources/order` body — applies one set to every task
 * on the order. Empty `items` unassigns it. `orderTaskCargoItemId` must be unset
 * on each item; split work goes through the per-task endpoint.
 */
export interface ReplaceOrderResourcesRequest {
  orderId: string;
  items: OrderTaskResourceInput[];
}

/**
 * PUT `/tms/api/v1/orderTaskResources/tasks` body — the cascade.
 *
 * Each task carries its OWN set, because the stops being cascaded onto keep
 * whatever else they already had (their own trailer, a split allocation) and only
 * the dropped slot changes. One transaction, so a cascade can't half-apply and
 * leave a journey changing hands twice.
 */
export interface ReplaceTasksResourcesRequest {
  tasks: { orderTaskId: string; items: OrderTaskResourceInput[] }[];
}

/**
 * PUT `/tms/api/v1/orderTaskResources/load` body — applies one set to every task
 * the load touches. Separate from the order route because a load can span several
 * orders. Empty `items` unassigns the manifest.
 */
export interface ReplaceLoadResourcesRequest {
  loadId: string;
  items: OrderTaskResourceInput[];
}

/** What a load line does on the run. Direct = a Collection + a Delivery line on one load. */
export type LoadLineLegRoleValue = 'Collection' | 'Delivery' | 'Trunk';

export interface LoadLine {
  id: string;
  loadId: string;
  orderTaskCargoItemId: string;
  legRole: LoadLineLegRoleValue;
  quantity?: number | null;
  loadingMetresM?: number | null;
  sequence: number;
}

/** A planned vehicle run / manifest with its (split-capable) lines embedded. */
export interface Load {
  id: string;
  organisationId: string;
  manifestNumber: string;
  depotSiteId: string;
  /** `yyyy-MM-dd`. */
  plannedDate: string;
  sequence: number;
  furthestPointAddressId?: string | null;
  backLoad: boolean;
  loadingMetresCapacity: number;
  status: LoadStatusValue;
  notes?: string | null;
  lines: LoadLine[];
  /**
   * Who and what is working this load, resolved BE-side through its lines to the
   * tasks that own the assignments.
   *
   * Read-only and derived — a load doesn't store resources. Writes go through
   * `replaceOrderTaskResources` / `replaceOrderResources`. This replaced the old
   * `truckResourceId` / `trailerResourceId` / `subcontractorPartyId` /
   * `truckRef` / `trailerRef` fields, which couldn't hold a driver at all.
   */
  resources: LoadResourceSummary[];
  createdUtc: string;
  updatedUtc?: string | null;
}

/** One assignment as seen from a load — the task scoping is collapsed away. */
export interface LoadResourceSummary {
  /**
   * Set when the assignment covers one cargo item rather than a whole task.
   * Present at load level so a client can detect split work and refuse to flatten
   * it through a load-scoped write.
   */
  orderTaskCargoItemId?: string | null;
  slot: AssignmentSlotValue;
  slotIndex: number;
  isSubcontractor: boolean;
  resourceId?: string | null;
  subcontractorPartyId?: string | null;
  subcontractorTrailerId?: string | null;
  externalReference?: string | null;
  referenceSnapshot?: string | null;
}

/** POST `/tms/api/v1/loads` body. `manifestNumber` is supplied by the caller. */
export interface CreateLoadRequest {
  organisationId: string;
  manifestNumber: string;
  depotSiteId: string;
  plannedDate: string;
  sequence: number;
  furthestPointAddressId?: string | null;
  backLoad: boolean;
  loadingMetresCapacity: number;
  truckResourceId?: string | null;
  trailerResourceId?: string | null;
  subcontractorPartyId?: string | null;
  truckRef?: string | null;
  trailerRef?: string | null;
  notes?: string | null;
}

/** PUT `/tms/api/v1/loads` body. `manifestNumber` is immutable. */
export interface UpdateLoadRequest {
  loadId: string;
  depotSiteId: string;
  plannedDate: string;
  sequence: number;
  furthestPointAddressId?: string | null;
  backLoad: boolean;
  loadingMetresCapacity: number;
  truckResourceId?: string | null;
  trailerResourceId?: string | null;
  subcontractorPartyId?: string | null;
  truckRef?: string | null;
  trailerRef?: string | null;
  status: LoadStatusValue;
  notes?: string | null;
}

export interface AssignLoadLineInput {
  orderTaskCargoItemId: string;
  legRole: LoadLineLegRoleValue;
  quantity?: number | null;
  loadingMetresM?: number | null;
  sequence: number;
}

/**
 * PUT `/tms/api/v1/loads/resequence-stops` body. Names every stop the load
 * touches, in the new running order. The BE maps that onto contiguous
 * `LoadLine.Sequence` values, keeping each stop's lines together.
 *
 * Distinct from `resequenceOrderTasks`: `OrderTask.sequence` counts from 1 within
 * its own order, so it can't express the order of a load spanning several.
 */
export interface ResequenceLoadStopsRequest {
  loadId: string;
  orderTaskIds: string[];
}

/** POST `/tms/api/v1/loads/assign` body. */
export interface AssignCargoItemsToLoadRequest {
  loadId: string;
  lines: AssignLoadLineInput[];
}

// ────────────────────────────────────────────────────────────────────────────
// Order Booking DTOs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Compact summary of an `OrderTask` carried inline on `OrderBooking`.
 * Just enough for the FE to render the booking pill against the right
 * ports + times without a follow-up call.
 */
export interface OrderBookingTaskSummary {
  id: string;
  type: TaskTypeValue;
  sequence: number;
  addressId: string;
  plannedDate?: string | null;
  plannedTime?: string | null;
  plannedDepartureTime?: string | null;
  state: TaskStateValue;
}

/** Wire-format projection of `OrderBooking`. */
export interface OrderBooking {
  id: string;
  organisationId: string;
  orderId: string;
  carrierPartyId?: string | null;
  /**
   * Optional shipping agent / freight forwarder. Persisted on the
   * booking row as a Supplier party id; the FE picker filters to the
   * org's ShippingAgency-subtype supplier roster.
   */
  agentPartyId?: string | null;
  carrierRouteId?: string | null;
  routeCode?: string | null;
  routeName?: string | null;
  carrierReference?: string | null;
  vessel?: string | null;
  voyageNumber?: string | null;
  sailDate?: string | null;
  sailTime?: string | null;
  typicalDurationMinutes?: number | null;
  typicalCheckInMinutes?: number | null;
  typicalDriveOffMinutes?: number | null;
  bookingType: BookingTypeValue;
  isAccompanied: boolean;
  numberOfDrivers?: number | null;
  vehicleRegistration?: string | null;
  trailerNumber?: string | null;
  customsReference?: string | null;
  clearanceStatus?: ClearanceStatusValue | null;
  notes?: string | null;
  /** My.Ferry's booking id; null when the booking was not sent through Ferry. */
  ferryBookingId?: string | null;
  /** The two paired BookingDeparture + BookingArrival tasks. */
  tasks: OrderBookingTaskSummary[];
  createdUtc: string;
  updatedUtc?: string | null;
}

/**
 * One row of the Bookings cockpit worklist (`GET /orderBookings/worklist`) — an
 * order active in the horizon plus its booking state. Whether it needs a ferry
 * is decided on the FE from the origin/destination address countries (see the
 * bookings `crossing` helper); the sailing recommendation (route / cost /
 * cutoff / alternatives) is the parked engine and is NOT returned here.
 */
export interface BookingWorklistItem {
  orderId: string;
  orderNumber: string;
  customerPartyId: string;
  status: OrderStatusValue;
  cargoDefinitionId: string;
  cargoDefinitionVariantId?: string | null;
  serviceTagIds: string[];
  /** Route endpoints — resolve these Directory addresses to read their countries. */
  originAddressId?: string | null;
  destinationAddressId?: string | null;
  /** Origin-port address of an already-planned ferry leg, if one exists. */
  departurePortAddressId?: string | null;
  deliveryDate?: string | null;
  deliveryTime?: string | null;
  hasBooking: boolean;
  booking?: OrderBooking | null;
}

/** Per-port task definition embedded in `CreateOrderBookingRequest`. */
export interface CreateOrderBookingTaskInput {
  addressId: string;
  sequence: number;
  plannedDate?: string | null;
  plannedTime?: string | null;
  plannedDepartureTime?: string | null;
  notes?: string | null;
}

/**
 * POST `/tms/api/v1/orderBookings` body. Creates the booking AND its
 * two paired BookingDeparture + BookingArrival tasks in one
 * transaction.
 */
export interface CreateOrderBookingRequest {
  orderId: string;
  carrierPartyId?: string | null;
  /**
   * Optional shipping agent / freight forwarder. Persisted on the
   * booking row as a Supplier party id; the FE picker filters to the
   * org's ShippingAgency-subtype supplier roster.
   */
  agentPartyId?: string | null;
  carrierRouteId?: string | null;
  routeCode?: string | null;
  routeName?: string | null;
  carrierReference?: string | null;
  vessel?: string | null;
  voyageNumber?: string | null;
  sailDate?: string | null;
  sailTime?: string | null;
  typicalDurationMinutes?: number | null;
  typicalCheckInMinutes?: number | null;
  typicalDriveOffMinutes?: number | null;
  bookingType: BookingTypeValue;
  isAccompanied: boolean;
  numberOfDrivers?: number | null;
  vehicleRegistration?: string | null;
  trailerNumber?: string | null;
  customsReference?: string | null;
  clearanceStatus?: ClearanceStatusValue | null;
  notes?: string | null;
  departure: CreateOrderBookingTaskInput;
  arrival: CreateOrderBookingTaskInput;
}

/**
 * PUT `/tms/api/v1/orderBookings` body. Booking-level fields only —
 * per-task edits go through `updateOrderTask`.
 */
export interface UpdateOrderBookingRequest {
  bookingId: string;
  carrierPartyId?: string | null;
  /**
   * Optional shipping agent / freight forwarder. Persisted on the
   * booking row as a Supplier party id; the FE picker filters to the
   * org's ShippingAgency-subtype supplier roster.
   */
  agentPartyId?: string | null;
  carrierRouteId?: string | null;
  routeCode?: string | null;
  routeName?: string | null;
  carrierReference?: string | null;
  vessel?: string | null;
  voyageNumber?: string | null;
  sailDate?: string | null;
  sailTime?: string | null;
  typicalDurationMinutes?: number | null;
  typicalCheckInMinutes?: number | null;
  typicalDriveOffMinutes?: number | null;
  bookingType: BookingTypeValue;
  isAccompanied: boolean;
  numberOfDrivers?: number | null;
  vehicleRegistration?: string | null;
  trailerNumber?: string | null;
  customsReference?: string | null;
  clearanceStatus?: ClearanceStatusValue | null;
  notes?: string | null;
  /** Link to the My.Ferry booking; null until the booking is sent. */
  ferryBookingId?: string | null;
}

// ────────────────────────────────────────────────────────────────────────────
// Order Document DTOs
// ────────────────────────────────────────────────────────────────────────────

/**
 * Wire-format projection of `OrderDocument`. Carries a freshly-minted
 * `downloadUrl` (Azure SAS, 10-minute expiry) so the FE can hand it to
 * `<a href>` directly — no second round-trip per row.
 */
export interface OrderDocument {
  id: string;
  organisationId: string;
  orderId: string;
  /** Set when the doc is task-scoped (POD for a specific delivery, etc.). */
  orderTaskId?: string | null;
  fileName: string;
  contentType: string;
  fileSizeBytes: number;
  contentHash?: string | null;
  blobName: string;
  containerName: string;
  description?: string | null;
  type: OrderDocumentTypeValue;
  downloadUrl?: string | null;
  createdUtc: string;
  updatedUtc?: string | null;
}

/**
 * Body for `uploadOrderDocument` — the helper assembles the
 * `FormData` from this typed shape. `file` carries the bytes; the rest
 * are string fields the BE pulls off the multipart form.
 */
export interface UploadOrderDocumentRequest {
  orderId: string;
  orderTaskId?: string | null;
  type: OrderDocumentTypeValue;
  description?: string | null;
  file: File;
}

/**
 * PUT `/tms/api/v1/orderDocuments` body — metadata only. Replacing the
 * file itself = delete + upload.
 */
export interface UpdateOrderDocumentRequest {
  documentId: string;
  orderTaskId?: string | null;
  description?: string | null;
  type: OrderDocumentTypeValue;
}

// ────────────────────────────────────────────────────────────────────────────
// Customer Rate Card
// ────────────────────────────────────────────────────────────────────────────

/** How a rate sheet turns quantity into a charge (wire = enum name). */
export type RateMethodValue = 'Unit' | 'Band' | 'RangeAccum' | 'RangeSplit';
/** What a rate-sheet line prices. */
export type RateSheetSubjectKindValue = 'Variant' | 'Uom';
/** How a per-customer additional charge is priced. */
export type AdditionalRateTypeValue = 'Flat' | 'PerHour' | 'PercentOfBase';
/** What a fuel surcharge is scoped to. */
export type FuelScopeKindValue = 'Country' | 'Region' | 'All';
/**
 * How strictly a route sheet's lane has to match a movement. `Exact` is a
 * direct run — collection at the origin, delivery at the destination, nothing
 * in between. `Contains` is the same endpoints with any stops along the way.
 * The mode is part of the lane's identity, so a customer can hold both for one
 * origin/destination and cargo type; `Exact` is the more specific match.
 */
export type LaneMatchModeValue = 'Exact' | 'Contains';
/** Which rate-card aggregate a delete targets. */
export type RateCardItemKindValue = 'RateSheet' | 'RouteSheet' | 'FuelSurcharge' | 'Additional';

/**
 * One end of a route lane — a Location picked from the organisation's
 * Directory addresses. `locationId` is the lane identity (what the BE's
 * same-lane conflict guard compares); `name` and the address fields are a
 * snapshot taken at create time, so a renamed location doesn't blank the grid
 * and the auto-charge matcher keeps its country / city keys. Both are null on
 * route sheets created before the Locations picker.
 */
export interface RegionEndpoint {
  locationId?: string | null;
  name?: string | null;
  country: string;
  city?: string | null;
  postcode?: string | null;
  placeId?: string | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface CustomerRateBand {
  id: string;
  fromQty: number;
  toQty?: number | null;
  rate: number;
  sequence: number;
}

export interface CustomerRateSheetLine {
  id: string;
  subjectKind: RateSheetSubjectKindValue;
  cargoVariantId?: string | null;
  uom?: RatingUomValue | null;
  minCharge?: number | null;
  maxCharge?: number | null;
  bands: CustomerRateBand[];
}

export interface CustomerRateSheet {
  id: string;
  cargoDefinitionId: string;
  rateMethod: RateMethodValue;
  currencyIsoCode: string;
  /**
   * Apply this sheet's price to a matching job on its own (the default), or
   * offer it and let the operator decide. The rate is agreed either way — this
   * is only about whether adding a task may spend it unattended.
   */
  autoApply: boolean;

  /** ISO calendar date (yyyy-MM-dd). */
  effectiveFrom: string;
  effectiveTo?: string | null;
  lines: CustomerRateSheetLine[];
}

export interface CustomerRouteSheetLine {
  id: string;
  cargoDefinitionId: string;
  uom: RatingUomValue;
  charge: number;
  extraDrop?: number | null;
}

export interface CustomerRouteSheet {
  id: string;
  currencyIsoCode: string;
  /**
   * Apply this sheet's price to a matching job on its own (the default), or
   * offer it and let the operator decide. The rate is agreed either way — this
   * is only about whether adding a task may spend it unattended.
   */
  autoApply: boolean;

  effectiveFrom: string;
  effectiveTo?: string | null;
  matchMode: LaneMatchModeValue;
  origin: RegionEndpoint;
  destination: RegionEndpoint;
  lines: CustomerRouteSheetLine[];
}

export interface CustomerFuelSurcharge {
  id: string;
  name: string;
  ratePercent: number;
  scopeKind: FuelScopeKindValue;
  /** Null when scopeKind is 'All' (flat rate, no territory). */
  scopeCode: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

export interface CustomerAdditionalCharge {
  id: string;
  chargeCostDefinitionId: string;
  chargeDisplayName: string;
  rateType: AdditionalRateTypeValue;
  rate: number;
  currencyIsoCode: string;
  fsApplicable: boolean;
}

/** A customer's full rate card — the four transport-pricing grids. */
export interface CustomerRateCard {
  customerPartyId: string;
  rateSheets: CustomerRateSheet[];
  routeSheets: CustomerRouteSheet[];
  fuelSurcharges: CustomerFuelSurcharge[];
  additionals: CustomerAdditionalCharge[];
}

// ── Create request shapes ───────────────────────────────────────────────────

export interface RateBandInput {
  fromQty: number;
  toQty?: number | null;
  rate: number;
  sequence: number;
}

export interface RateSheetLineInput {
  subjectKind: RateSheetSubjectKindValue;
  cargoVariantId?: string | null;
  uom?: RatingUomValue | null;
  minCharge?: number | null;
  maxCharge?: number | null;
  bands: RateBandInput[];
}

export interface CreateCustomerRateSheetRequest {
  organisationId: string;
  customerPartyId: string;
  cargoDefinitionId: string;
  rateMethod: RateMethodValue;
  currencyIsoCode: string;
  autoApply: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  lines: RateSheetLineInput[];
}

export interface RouteSheetLineInput {
  cargoDefinitionId: string;
  uom: RatingUomValue;
  charge: number;
  extraDrop?: number | null;
}

export interface CreateCustomerRouteSheetRequest {
  organisationId: string;
  customerPartyId: string;
  currencyIsoCode: string;
  autoApply: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  matchMode: LaneMatchModeValue;
  origin: RegionEndpoint;
  destination: RegionEndpoint;
  lines: RouteSheetLineInput[];
}

export interface CreateCustomerFuelSurchargeRequest {
  organisationId: string;
  customerPartyId: string;
  name: string;
  ratePercent: number;
  scopeKind: FuelScopeKindValue;
  /** Null when scopeKind is 'All' (flat rate, no territory). */
  scopeCode: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

/** PUT `/tms/api/v1/customerRates/fuel-surcharges` — edit an existing surcharge. */
/**
 * PUT `/customerRates/rate-sheets` body — a FULL REPLACE of the sheet: cargo
 * type, method, window and the whole ladder of lines + bands. The owning org,
 * customer and currency are read from the stored row, so they aren't sent.
 */
export interface UpdateCustomerRateSheetRequest {
  id: string;
  cargoDefinitionId: string;
  rateMethod: RateMethodValue;
  autoApply: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  lines: RateSheetLineInput[];
}

/**
 * PUT `/customerRates/route-sheets` body — a FULL REPLACE of the lane, its
 * window and every cargo line on it. Dropping a cargo type from a lane is an
 * edit that omits its line.
 */
export interface UpdateCustomerRouteSheetRequest {
  id: string;
  autoApply: boolean;
  effectiveFrom: string;
  effectiveTo?: string | null;
  matchMode: LaneMatchModeValue;
  origin: RegionEndpoint;
  destination: RegionEndpoint;
  lines: RouteSheetLineInput[];
}

export interface UpdateCustomerFuelSurchargeRequest {
  id: string;
  name: string;
  ratePercent: number;
  scopeKind: FuelScopeKindValue;
  /** Null when scopeKind is 'All' (flat rate, no territory). */
  scopeCode: string | null;
  effectiveFrom: string;
  effectiveTo?: string | null;
}

export interface CreateCustomerAdditionalChargeRequest {
  organisationId: string;
  customerPartyId: string;
  chargeCostDefinitionId: string;
  chargeDisplayName: string;
  rateType: AdditionalRateTypeValue;
  rate: number;
  currencyIsoCode: string;
  fsApplicable: boolean;
}

